import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ClipboardList, RefreshCw, Users, BookOpen, AlertTriangle, Sparkles,
  Trash2, ChevronDown, ChevronUp, CheckCircle, Lightbulb, Settings, Loader2, Bot, Paintbrush,
  Plus, GitMerge, Lock, Unlock, X, MonitorPlay, ImageIcon, Pencil, FolderOpen, Wand2, ArrowRight,
  MessageSquare, ShieldCheck, Tag, Copy, Search, Layers, GitBranch,
  StickyNote, BookMarked,
} from 'lucide-react';
import { StoryRow, StoryTableHead, STORY_COLUMNS, priorityConfig, storyText } from './live-session/StoryRow';
import { RaidEditModal } from './live-session/raid/RaidEditModal';
import { RaidCardSection } from './live-session/raid/RaidCardSection';
import { LinkCandidate } from './live-session/raid/RaidLinkPicker';
import { useRaidItems, useRaidIndex, useRaidMutations, lookupRaid } from '../hooks/useRaidItems';
import { RaidItem, RaidStatus, RaidLink, RaidLinkTargetType } from '../types/raid';
import { RaidItemInput } from '../services/raid';
import { useMilestones, useMilestoneMutations } from '../hooks/useMilestones';
import { toast } from 'sonner';
import { MilestonePicker } from './live-session/MilestonePicker';
import { PersonaReviewModal } from './live-session/PersonaReviewModal';
import { StoryEditModal } from './live-session/StoryEditModal';
import { NFREditModal } from './live-session/NFREditModal';
import { FeatureEditModal } from './live-session/FeatureEditModal';
import { ADREditModal } from './live-session/ADREditModal';
import { MergePreviewModal } from './live-session/MergePreviewModal';
import { ProductRequirements, NFR, UserStory, Feature, ADR, extractNFRs, getFeatures, createFeature, updateFeature, deleteFeature, extractADRs, getADRs, createADR, updateADR, deleteADR } from '../services/workshop';
import { api } from '../services/api';
import { AvatarDefinition } from '../types';
import { QuickCreateAvatarModal } from './QuickCreateAvatarModal';
import { GenerateImageModal } from './GenerateImageModal';
import type { GenerateImageParams } from './GenerateImageModal';
import { getImageTaskStatus } from '../services/imageGen';

type StoryPriority = 'high' | 'medium' | 'low';
type DeleteConfirmType = 'persona' | 'story' | 'nfr' | 'feature' | 'adr';

interface RequirementsPanelProps {
  requirements: ProductRequirements | null;
  loading: boolean;
  hasTranscript: boolean;
  removedRequirementIds: Set<string>;
  removedPersonaIds: Set<string>;
  onRefresh: () => void;
  onRemoveRequirement: (id: string) => void;
  onRemovePersona: (id: string) => void;
  onClearAll?: () => Promise<void>;
  projectId?: string;
  sessionId?: string;
  onAvatarCreated?: (avatar: AvatarDefinition) => void;
  onStartOpenDesignTask?: (storyText: string, skillId: string | null) => Promise<void>;
  onActivePersonaChange?: (personaId: string | null) => void;
  onStoriesScroll?: (scrollTop: number) => void;
  onPushToScreen?: () => void;
  onGenerateImage?: (params: GenerateImageParams) => Promise<string>;
  onReloadData?: () => Promise<void>;
  onOpenDeepDive?: () => void;
}

interface ChallengeArea { areaId: string; title: string; summary: string; keyTopics: string[]; relevantPersonas: string[]; }

interface ChallengeAreasSectionProps {
  challengeAreas: ChallengeArea[];
  generatingImageFor: string | null;
  onGenerateAllImage?: () => void;
  onGenerateAreaImage?: (area: ChallengeArea) => void;
}

function ChallengeAreasSection({ challengeAreas, generatingImageFor, onGenerateAllImage, onGenerateAreaImage }: ChallengeAreasSectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedAreaId, setExpandedAreaId] = useState<string | null>(null);
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full px-5 py-3 border-b border-[#e2e8f0] bg-slate-50/50 flex items-center gap-2 hover:bg-slate-100/60 transition-colors"
      >
        <FolderOpen size={14} className="text-primary" />
        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Challenge Areas</span>
        <span className="text-xs text-slate-400">({challengeAreas.length})</span>
        {onGenerateAllImage && (
          <button
            onClick={e => { e.stopPropagation(); onGenerateAllImage(); }}
            disabled={!!generatingImageFor}
            className="ml-2 flex items-center gap-1 px-2 py-0.5 rounded-full border border-orange-200 bg-orange-50 text-orange-600 text-[10px] font-semibold hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {generatingImageFor === 'challenge-all' ? (
              <Loader2 size={10} className="animate-spin" />
            ) : (
              <ImageIcon size={10} />
            )}
            {generatingImageFor === 'challenge-all' ? 'Generating…' : 'Generate Image'}
          </button>
        )}
        <span className="ml-auto text-slate-400">{collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}</span>
      </button>
      {!collapsed && (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {challengeAreas.map(area => {
            const isExpanded = expandedAreaId === area.areaId;
            const isGenerating = generatingImageFor === `challenge-${area.areaId}`;
            return (
              <div key={area.areaId} className="flex flex-col gap-2 bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
                <button
                  onClick={() => setExpandedAreaId(isExpanded ? null : area.areaId)}
                  className="flex items-center justify-between px-4 pt-4 pb-2 text-left w-full"
                >
                  <p className="text-sm font-bold text-navy leading-tight">{area.title}</p>
                  <span className="text-slate-400 ml-2 flex-shrink-0">{isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</span>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 flex flex-col gap-2">
                    <p className="text-xs text-slate-500 leading-relaxed">{area.summary}</p>
                    {area.keyTopics.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {area.keyTopics.map(t => (
                          <span key={t} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold">{t}</span>
                        ))}
                      </div>
                    )}
                    {area.relevantPersonas.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {area.relevantPersonas.map(p => (
                          <span key={p} className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 text-[10px] font-semibold">{p}</span>
                        ))}
                      </div>
                    )}
                    {onGenerateAreaImage && (
                      <button
                        onClick={() => onGenerateAreaImage(area)}
                        disabled={!!generatingImageFor}
                        className="mt-1 self-start flex items-center gap-1.5 px-3 py-1 rounded-full border border-orange-200 bg-orange-50 text-orange-600 text-xs font-semibold hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                      >
                        {isGenerating ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                        {isGenerating ? 'Generating…' : 'Generate Image'}
                      </button>
                    )}
                  </div>
                )}
                {!isExpanded && (
                  <div className="px-4 pb-3">
                    <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{area.summary}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function RequirementsPanel({
  requirements,
  loading,
  hasTranscript,
  removedRequirementIds,
  removedPersonaIds,
  onRefresh,
  onRemoveRequirement,
  onRemovePersona,
  onClearAll,
  projectId,
  sessionId,
  onAvatarCreated,
  onStartOpenDesignTask,
  onActivePersonaChange,
  onStoriesScroll,
  onPushToScreen,
  onGenerateImage,
  onReloadData,
  onOpenDeepDive,
}: RequirementsPanelProps) {
  // null = first persona; ?reqTab=adrs (or nfrs/features/...) opens a sub-tab directly, e.g. from the Facilitator HUD
  const [activeTab, setActiveTab] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('reqTab')
  );
  const [nfrLoading, setNfrLoading] = useState(false);

  const runNfrExtraction = useCallback(async () => {
    if (!sessionId || nfrLoading) return;
    setNfrLoading(true);
    setShowNfrRerunConfirm(false);
    try {
      const beforeCount = requirements?.nonFunctionalRequirements?.length ?? 0;
      await extractNFRs(sessionId);
      const deadline = Date.now() + 180000;
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/api/sessions/${sessionId}/requirements`);
          const afterCount = (res.data.requirements?.nonFunctionalRequirements ?? []).length;
          if (afterCount !== beforeCount || Date.now() > deadline) {
            clearInterval(poll);
            setNfrLoading(false);
            await onReloadData?.();
          }
        } catch {
          if (Date.now() > deadline) { clearInterval(poll); setNfrLoading(false); }
        }
      }, 5000);
    } catch (e) {
      console.error('NFR extraction failed', e);
      setNfrLoading(false);
    }
  }, [sessionId, nfrLoading, requirements, onReloadData]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [expandedStoryIds, setExpandedStoryIds] = useState<Set<string>>(new Set());
  const [openDesigningStoryId, setOpenDesigningStoryId] = useState<string | null>(null);
  const [openDesignPickerTarget, setOpenDesignPickerTarget] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: DeleteConfirmType;
    id: string;
    label: string;
  } | null>(null);
  const [makeAvatarPersona, setMakeAvatarPersona] = useState<{
    personaId: string;
    name: string;
    role: string;
    goals?: string[];
    painPoints?: string[];
  } | null>(null);

  // Persona creation lock
  const [allowNewPersonaCreation, setAllowNewPersonaCreation] = useState(true);
  const [lockLoading, setLockLoading] = useState(false);

  // Add persona modal
  const [showAddPersona, setShowAddPersona] = useState(false);
  const [addPersonaSaving, setAddPersonaSaving] = useState(false);
  const [newPersonaName, setNewPersonaName] = useState('');
  const [newPersonaRole, setNewPersonaRole] = useState('');
  const [newPersonaGoals, setNewPersonaGoals] = useState('');
  const [newPersonaPainPoints, setNewPersonaPainPoints] = useState('');

  // Add story modal
  const [addStoryForPersona, setAddStoryForPersona] = useState<string | null>(null);
  const [addStorySaving, setAddStorySaving] = useState(false);
  const [newStoryText, setNewStoryText] = useState('');
  const [newStoryCriteria, setNewStoryCriteria] = useState('');
  const [newStoryPriority, setNewStoryPriority] = useState<StoryPriority>('medium');

  // Merge picker
  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null);

  // Inline About editing
  const [localPersonaDescriptions, setLocalPersonaDescriptions] = useState<Map<string, string>>(new Map());
  const [editingAboutPersonaId, setEditingAboutPersonaId] = useState<string | null>(null);
  const [editAboutDraft, setEditAboutDraft] = useState('');
  const descriptionsLoadedRef = useRef(false);

  // Challenge areas
  const [challengeAreas, setChallengeAreas] = useState<ChallengeArea[]>([]);
  const challengeAreasLoadedRef = useRef(false);

  // Per-persona story re-extraction
  const [extractingStoriesFor, setExtractingStoriesFor] = useState<string | null>(null);

  // Persona Review Modal
  const [reviewModalPersonaId, setReviewModalPersonaId] = useState<string | null>(null);

  // Story edit modal
  const [storyEditTarget, setStoryEditTarget] = useState<UserStory | null>(null);
  const [personaInfoCollapsed, setPersonaInfoCollapsed] = useState(false);

  // Story table search / filter / sort
  const [storySearch, setStorySearch] = useState('');
  const [storyPriorityFilter, setStoryPriorityFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [storySortBy, setStorySortBy] = useState<'id' | 'priority' | 'story' | null>(null);
  const [storySortDir, setStorySortDir] = useState<'asc' | 'desc'>('asc');

  const handleStorySort = (col: 'id' | 'priority' | 'story') => {
    if (storySortBy === col) {
      setStorySortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setStorySortBy(col);
      setStorySortDir('asc');
    }
  };

  const storySortIcon = (col: 'id' | 'priority' | 'story') => {
    if (storySortBy !== col) return null;
    return storySortDir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />;
  };

  // ==========================================================================
  // RAID items
  //
  // One fetch for the session, joined to artefacts in memory. Mutations patch
  // the query cache rather than invalidating, so resolving an item on a card
  // does not reload the requirements blob underneath it.
  // ==========================================================================
  const raidScope = useMemo(() => ({ sessionId }), [sessionId]);
  const { data: raidData } = useRaidItems(raidScope);
  const raidItems = useMemo(() => raidData?.raidItems ?? [], [raidData]);
  const raidIndex = useRaidIndex(raidItems);
  const raidMutations = useRaidMutations(raidScope);
  const [expandedRaidTargets, setExpandedRaidTargets] = useState<Set<string>>(new Set());
  const [raidModal, setRaidModal] = useState<
    { mode: 'edit'; item: RaidItem } | { mode: 'create'; links: RaidLink[] } | null
  >(null);

  // ==========================================================================
  // Delivery milestones
  //
  // Project-level, so they persist across sessions. Tagging is per item with
  // no cascade: setting a feature's phase does not move its stories, it just
  // offers a one-click bulk action for them.
  // ==========================================================================
  const { data: milestones = [] } = useMilestones(projectId);
  const milestoneMutations = useMilestoneMutations(projectId);
  const [bulkTagPrompt, setBulkTagPrompt] = useState<{ feature: Feature; milestoneId: string } | null>(null);

  const toggleRaidTarget = useCallback((key: string) => {
    setExpandedRaidTargets(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleResolveRaid = useCallback((raidId: string, status: RaidStatus, resolution: string) => {
    raidMutations.resolve.mutate({ raidId, status, resolution });
  }, [raidMutations.resolve]);

  const handleSaveRaid = useCallback(async (input: RaidItemInput) => {
    if (raidModal?.mode === 'edit') {
      await raidMutations.update.mutateAsync({ raidId: raidModal.item.raidId, patch: input });
    } else {
      await raidMutations.create.mutateAsync(input);
    }
    setRaidModal(null);
  }, [raidModal, raidMutations.create, raidMutations.update]);

  const handleDeleteRaid = useCallback(async () => {
    if (raidModal?.mode !== 'edit') return;
    await raidMutations.remove.mutateAsync(raidModal.item.raidId);
    setRaidModal(null);
  }, [raidModal, raidMutations.remove]);

  // NFR edit modal
  const [nfrEditTarget, setNfrEditTarget] = useState<NFR | null>(null);
  const [nfrCreateMode, setNfrCreateMode] = useState(false);
  const [showNfrRerunConfirm, setShowNfrRerunConfirm] = useState(false);

  // Features
  const [features, setFeatures] = useState<Feature[]>([]);
  const [featureEditTarget, setFeatureEditTarget] = useState<Feature | null>(null);
  const [featureCreateMode, setFeatureCreateMode] = useState(false);
  const featuresLoadedRef = useRef(false);

  // ADRs
  const [adrs, setAdrs] = useState<ADR[]>([]);
  const [adrEditTarget, setAdrEditTarget] = useState<ADR | null>(null);
  const [adrCreateMode, setAdrCreateMode] = useState(false);
  const adrsLoadedRef = useRef(false);
  const [adrLoading, setAdrLoading] = useState(false);
  const [showAdrRerunConfirm, setShowAdrRerunConfirm] = useState(false);

  // Story selection + bulk actions
  const [selectedStoryIds, setSelectedStoryIds] = useState<Set<string>>(new Set());
  const [bulkStoryAction, setBulkStoryAction] = useState<'merge' | 'delete' | 'priority' | 'move' | null>(null);
  const [bulkStoryConfirm, setBulkStoryConfirm] = useState(false);
  const [bulkPriorityValue, setBulkPriorityValue] = useState<StoryPriority>('medium');
  const [bulkMovePersona, setBulkMovePersona] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  // Story merge state
  const [storyMergeLoading, setStoryMergeLoading] = useState(false);
  const [storyMergeResult, setStoryMergeResult] = useState<any>(null);
  const [storyMergeError, setStoryMergeError] = useState<string | null>(null);
  const [showStoryMergeModal, setShowStoryMergeModal] = useState(false);

  // NFR selection + bulk actions
  const [selectedNfrIds, setSelectedNfrIds] = useState<Set<string>>(new Set());
  const [bulkNfrAction, setBulkNfrAction] = useState<'merge' | 'delete' | 'priority' | null>(null);
  const [bulkNfrConfirm, setBulkNfrConfirm] = useState(false);
  const [bulkNfrPriorityValue, setBulkNfrPriorityValue] = useState<StoryPriority>('medium');
  const [bulkNfrActionLoading, setBulkNfrActionLoading] = useState(false);
  // NFR merge state
  const [nfrMergeLoading, setNfrMergeLoading] = useState(false);
  const [nfrMergeResult, setNfrMergeResult] = useState<any>(null);
  const [nfrMergeError, setNfrMergeError] = useState<string | null>(null);
  const [showNfrMergeModal, setShowNfrMergeModal] = useState(false);

  // Image generation
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageGenLoading, setImageGenLoading] = useState(false);
  const [imageGenCountdown, setImageGenCountdown] = useState(0);
  const [generatingImageFor, setGeneratingImageFor] = useState<string | null>(null);
  const imageGenCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startImageGenCountdown = () => {
    setImageGenCountdown(0);
    if (imageGenCountdownRef.current) clearInterval(imageGenCountdownRef.current);
    imageGenCountdownRef.current = setInterval(() => {
      setImageGenCountdown(prev => prev + 1);
    }, 1000);
  };

  const stopImageGenCountdown = () => {
    if (imageGenCountdownRef.current) {
      clearInterval(imageGenCountdownRef.current);
      imageGenCountdownRef.current = null;
    }
    setImageGenCountdown(0);
  };

  const pollImageTask = async (imageId: string, resolveOnComplete: () => void) => {
    if (!sessionId) return;
    const maxAttempts = 120;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const result = await getImageTaskStatus(sessionId, imageId);
        if (result.status === 'complete' || result.status === 'failed') {
          resolveOnComplete();
          return;
        }
      } catch { /* continue polling */ }
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        resolveOnComplete();
      }
    };
    setTimeout(poll, 2000);
  };

  useEffect(() => {
    if (!sessionId || !requirements || descriptionsLoadedRef.current) return;
    descriptionsLoadedRef.current = true;
    api.get(`/api/sessions/${sessionId}/personas`)
      .then(res => {
        const map = new Map<string, string>();
        for (const p of (res.data.personas || [])) {
          if (p.description != null) map.set(p.id, p.description);
        }
        setLocalPersonaDescriptions(map);
      })
      .catch(() => {});
  }, [sessionId, requirements]);

  useEffect(() => {
    if (!sessionId || challengeAreasLoadedRef.current) return;
    challengeAreasLoadedRef.current = true;
    api.get(`/api/sessions/${sessionId}/challenge-areas`)
      .then(res => setChallengeAreas(res.data.challengeAreas || []))
      .catch(() => {});
  }, [sessionId]);

  const loadFeatures = useCallback(async () => {
    if (!sessionId) return;
    try {
      const result = await getFeatures(sessionId);
      setFeatures(result);
    } catch {
      // Non-fatal — features are additive
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || featuresLoadedRef.current) return;
    featuresLoadedRef.current = true;
    loadFeatures();
  }, [sessionId, loadFeatures]);

  // Feature edit/create modal save
  const handleSaveFeatureModal = useCallback(async (featureId: string, patch: Partial<Feature>) => {
    if (!sessionId) return;
    if (featureCreateMode) {
      await createFeature(sessionId, patch);
      setFeatureCreateMode(false);
    } else {
      await updateFeature(sessionId, featureId, patch);
    }
    await loadFeatures();
  }, [sessionId, featureCreateMode, loadFeatures]);

  const loadADRs = useCallback(async () => {
    if (!sessionId) return;
    try {
      const result = await getADRs(sessionId);
      setAdrs(result);
    } catch {
      // Non-fatal — ADRs are additive
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || adrsLoadedRef.current) return;
    adrsLoadedRef.current = true;
    loadADRs();
  }, [sessionId, loadADRs]);

  // ADR edit/create modal save
  const handleSaveAdrModal = useCallback(async (adrId: string, patch: Partial<ADR>) => {
    if (!sessionId) return;
    if (adrCreateMode) {
      await createADR(sessionId, patch);
      setAdrCreateMode(false);
    } else {
      await updateADR(sessionId, adrId, patch);
    }
    await loadADRs();
  }, [sessionId, adrCreateMode, loadADRs]);

  const runAdrExtraction = useCallback(async () => {
    if (!sessionId || adrLoading) return;
    setAdrLoading(true);
    setShowAdrRerunConfirm(false);
    try {
      const beforeCount = adrs.length;
      await extractADRs(sessionId);
      const deadline = Date.now() + 180000;
      const poll = setInterval(async () => {
        try {
          const result = await getADRs(sessionId);
          if (result.length !== beforeCount || Date.now() > deadline) {
            clearInterval(poll);
            setAdrLoading(false);
            setAdrs(result);
          }
        } catch {
          if (Date.now() > deadline) { clearInterval(poll); setAdrLoading(false); }
        }
      }, 5000);
    } catch (e) {
      console.error('ADR extraction failed', e);
      setAdrLoading(false);
    }
  }, [sessionId, adrLoading, adrs]);

  // Re-fetch challenge areas when requirements update, then poll — challenge areas
  // extraction is auto-triggered *after* requirements extraction completes so data
  // arrives 30-90s later.
  useEffect(() => {
    if (!sessionId || !requirements) return;
    const fetch = () => api.get(`/api/sessions/${sessionId}/challenge-areas`)
      .then(res => setChallengeAreas(res.data.challengeAreas || []))
      .catch(() => {});
    fetch();
    const t1 = setTimeout(fetch, 15000);
    const t2 = setTimeout(fetch, 45000);
    const t3 = setTimeout(fetch, 90000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [sessionId, requirements?.generatedAt]);

  const handleGenerateImageFromModal = async (params: GenerateImageParams) => {
    if (!onGenerateImage) return;
    setImageGenLoading(true);
    startImageGenCountdown();
    try {
      const imageId = await onGenerateImage(params);
      setShowImageModal(false);
      await new Promise<void>(resolve => pollImageTask(imageId, resolve));
    } finally {
      setImageGenLoading(false);
      stopImageGenCountdown();
    }
  };

  const handleGeneratePersonaImage = async (persona: { id: string; name: string; role: string; goals: string[]; painPoints: string[] }) => {
    if (!onGenerateImage) return;
    const prompt = [
      `Create a character sketch doodle of ${persona.name}: A ${persona.role}.`,
      persona.goals.length > 0 ? `Goals: ${persona.goals.join(', ')}.` : '',
      persona.painPoints.length > 0 ? `Pain points and challenges: ${persona.painPoints.join(', ')}.` : '',
      'Style: sympathetic, human-centred, capturing their professional context, pressures, and aspirations. Include visual metaphors for their key challenges and desires.',
    ].filter(Boolean).join(' ');

    setGeneratingImageFor(`persona-${persona.id}`);
    startImageGenCountdown();
    try {
      const imageId = await onGenerateImage({ prompt, promptType: 'persona', aspectRatio: 'widescreen' });
      await new Promise<void>(resolve => pollImageTask(imageId, resolve));
    } finally {
      setGeneratingImageFor(null);
      stopImageGenCountdown();
    }
  };

  const handleGenerateChallengeAreaImage = async (area: ChallengeArea) => {
    if (!onGenerateImage) return;
    const prompt = [
      `Create a character sketch doodle illustrating the challenge area: "${area.title}".`,
      area.summary ? area.summary : '',
      area.keyTopics.length > 0 ? `Key themes: ${area.keyTopics.join(', ')}.` : '',
      area.relevantPersonas.length > 0 ? `Affected personas: ${area.relevantPersonas.join(', ')}.` : '',
      'Style: sympathetic, human-centred doodle capturing the pressures, barriers, and human impact of this challenge. Include visual metaphors for the core tension.',
    ].filter(Boolean).join(' ');

    setGeneratingImageFor(`challenge-${area.areaId}`);
    startImageGenCountdown();
    try {
      const imageId = await onGenerateImage({ prompt, promptType: 'custom', aspectRatio: 'widescreen' });
      await new Promise<void>(resolve => pollImageTask(imageId, resolve));
    } finally {
      setGeneratingImageFor(null);
      stopImageGenCountdown();
    }
  };

  const handleGenerateAllChallengeAreasImage = async () => {
    if (!onGenerateImage || challengeAreas.length === 0) return;
    const suffix = '\nStyle: sympathetic, human-centred composition showing the interconnected pressures and pain points across all challenge areas. Use visual metaphors and a panoramic scene that brings each challenge to life.';
    const prefix = 'Create a character sketch doodle that captures all the following challenge areas from a workshop session:\n';
    const budget = 1950 - prefix.length - suffix.length;
    // Build area lines, truncating summaries to fit within the 2000 char backend limit
    const lines: string[] = [];
    let used = 0;
    for (const a of challengeAreas) {
      const keyTopicsSuffix = a.keyTopics.length > 0 ? ` (themes: ${a.keyTopics.join(', ')})` : '';
      const full = `- ${a.title}: ${a.summary}${keyTopicsSuffix}`;
      const compact = `- ${a.title}`;
      let line: string | null;
      if (used + full.length <= budget) line = full;
      else if (used + compact.length <= budget) line = compact;
      else line = null;
      if (line === null) break;
      lines.push(line);
      used += line.length + 1;
    }
    const prompt = prefix + lines.join('\n') + suffix;

    setGeneratingImageFor('challenge-all');
    startImageGenCountdown();
    try {
      const imageId = await onGenerateImage({ prompt, promptType: 'custom', aspectRatio: 'widescreen' });
      await new Promise<void>(resolve => pollImageTask(imageId, resolve));
    } finally {
      setGeneratingImageFor(null);
      stopImageGenCountdown();
    }
  };

  // Story edit modal save
  const handleSaveStoryModal = useCallback(async (storyId: string, patch: Partial<UserStory>) => {
    if (!sessionId) return;
    if (storyId.startsWith('CLONE-')) {
      await api.post(`/api/sessions/${sessionId}/user-stories`, patch);
    } else {
      await api.patch(`/api/sessions/${sessionId}/user-stories/${storyId}`, patch);
    }
    await onReloadData?.();
  }, [sessionId, onReloadData]);

  // Lock/unlock a story — blocks every other user/AI edit path until unlocked again
  const handleToggleStoryLock = useCallback(async (story: UserStory & { locked?: boolean }) => {
    if (!sessionId) return;
    const endpoint = story.locked ? 'unlock' : 'lock';
    await api.patch(`/api/sessions/${sessionId}/user-stories/${story.id}/${endpoint}`, {});
    await onReloadData?.();
  }, [sessionId, onReloadData]);

  // Ask session avatars to review a story, a selection, or a whole persona's stories.
  // Results land asynchronously as pending contributions (Avatar Contributions tab),
  // tagged with reviewScope — this call only fires the request.
  const triggerAvatarReview = useCallback(async (scope: { type: 'story' | 'selection' | 'persona'; storyId?: string; storyIds?: string[]; persona?: string }) => {
    if (!sessionId) return;
    try {
      const res = await api.post(`/api/sessions/${sessionId}/user-stories/avatar-review`, { scope });
      toast.success(`Asked ${res.data.avatarCount} avatar${res.data.avatarCount === 1 ? '' : 's'} to review ${res.data.storyCount} stor${res.data.storyCount === 1 ? 'y' : 'ies'} — check Avatar Contributions for results`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to request avatar review');
    }
  }, [sessionId]);

  // NFR edit modal save (handles both create and edit)
  const handleSaveNFRModal = useCallback(async (nfrId: string, patch: Partial<NFR>) => {
    if (!sessionId) return;
    if (nfrCreateMode) {
      await api.post(`/api/sessions/${sessionId}/nfrs`, patch);
      setNfrCreateMode(false);
    } else {
      await api.patch(`/api/sessions/${sessionId}/nfrs/${nfrId}`, patch);
    }
    await onReloadData?.();
  }, [sessionId, nfrCreateMode, onReloadData]);

  // Story selection helpers
  const toggleStorySelection = (id: string) => setSelectedStoryIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearStorySelection = () => setSelectedStoryIds(new Set());

  // NFR selection helpers
  const toggleNfrSelection = (id: string) => setSelectedNfrIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearNfrSelection = () => setSelectedNfrIds(new Set());

  // Execute story bulk action after confirmation
  const executeBulkStoryAction = useCallback(async () => {
    if (!sessionId || !bulkStoryAction) return;
    setBulkActionLoading(true);
    const ids = Array.from(selectedStoryIds);
    try {
      if (bulkStoryAction === 'delete') {
        await Promise.all(ids.map(id => api.delete(`/api/sessions/${sessionId}/user-stories/${id}`)));
      } else if (bulkStoryAction === 'priority') {
        await Promise.all(ids.map(id => api.patch(`/api/sessions/${sessionId}/user-stories/${id}`, { priority: bulkPriorityValue })));
      } else if (bulkStoryAction === 'move') {
        await Promise.all(ids.map(id => api.patch(`/api/sessions/${sessionId}/user-stories/${id}`, { persona: bulkMovePersona })));
      }
      clearStorySelection();
      setBulkStoryAction(null);
      setBulkStoryConfirm(false);
      await onReloadData?.();
    } finally {
      setBulkActionLoading(false);
    }
  }, [sessionId, bulkStoryAction, selectedStoryIds, bulkPriorityValue, bulkMovePersona, onReloadData]);

  // Trigger story AI merge
  const triggerStoryMerge = useCallback(async () => {
    if (!sessionId) return;
    const ids = Array.from(selectedStoryIds);
    setStoryMergeLoading(true);
    setStoryMergeResult(null);
    setStoryMergeError(null);
    setShowStoryMergeModal(true);
    try {
      const res = await api.post(`/api/sessions/${sessionId}/user-stories/merge`, { storyIds: ids });
      setStoryMergeResult(res.data.merged);
    } catch (e: any) {
      setStoryMergeError(e?.response?.data?.error || 'Merge failed');
    } finally {
      setStoryMergeLoading(false);
    }
  }, [sessionId, selectedStoryIds]);

  // Confirm story merge — save merged, delete originals
  const confirmStoryMerge = useCallback(async (edited: any) => {
    if (!sessionId) return;
    const ids = Array.from(selectedStoryIds);
    await api.patch(`/api/sessions/${sessionId}/user-stories/${ids[0]}`, {
      story: edited.story,
      acceptanceCriteria: edited.acceptanceCriteria,
      priority: edited.priority,
    });
    await Promise.all(ids.slice(1).map(id => api.delete(`/api/sessions/${sessionId}/user-stories/${id}`)));
    clearStorySelection();
    setShowStoryMergeModal(false);
    await onReloadData?.();
  }, [sessionId, selectedStoryIds, onReloadData]);

  // Execute NFR bulk action after confirmation
  const executeBulkNfrAction = useCallback(async () => {
    if (!sessionId || !bulkNfrAction) return;
    setBulkNfrActionLoading(true);
    const ids = Array.from(selectedNfrIds);
    try {
      if (bulkNfrAction === 'delete') {
        await Promise.all(ids.map(id => api.delete(`/api/sessions/${sessionId}/nfrs/${id}`)));
      } else if (bulkNfrAction === 'priority') {
        await Promise.all(ids.map(id => api.patch(`/api/sessions/${sessionId}/nfrs/${id}`, { priority: bulkNfrPriorityValue })));
      }
      clearNfrSelection();
      setBulkNfrAction(null);
      setBulkNfrConfirm(false);
      await onReloadData?.();
    } finally {
      setBulkNfrActionLoading(false);
    }
  }, [sessionId, bulkNfrAction, selectedNfrIds, bulkNfrPriorityValue, onReloadData]);

  // Trigger NFR AI merge
  const triggerNfrMerge = useCallback(async () => {
    if (!sessionId) return;
    const ids = Array.from(selectedNfrIds);
    setNfrMergeLoading(true);
    setNfrMergeResult(null);
    setNfrMergeError(null);
    setShowNfrMergeModal(true);
    try {
      const res = await api.post(`/api/sessions/${sessionId}/nfrs/merge`, { nfrIds: ids });
      setNfrMergeResult(res.data.merged);
    } catch (e: any) {
      setNfrMergeError(e?.response?.data?.error || 'Merge failed');
    } finally {
      setNfrMergeLoading(false);
    }
  }, [sessionId, selectedNfrIds]);

  // Confirm NFR merge
  const confirmNfrMerge = useCallback(async (edited: any) => {
    if (!sessionId) return;
    const ids = Array.from(selectedNfrIds);
    await api.patch(`/api/sessions/${sessionId}/nfrs/${ids[0]}`, {
      title: edited.title,
      description: edited.description,
      priority: edited.priority,
      category: edited.category,
      acceptanceCriteria: edited.acceptanceCriteria,
    });
    await Promise.all(ids.slice(1).map(id => api.delete(`/api/sessions/${sessionId}/nfrs/${id}`)));
    clearNfrSelection();
    setShowNfrMergeModal(false);
    await onReloadData?.();
  }, [sessionId, selectedNfrIds, onReloadData]);

  // Scroll ref for stories content area
  const storiesScrollRef = useRef<HTMLDivElement>(null);
  const scrollDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleStoriesScroll = useCallback(() => {
    if (!onStoriesScroll || !storiesScrollRef.current) return;
    const scrollTop = storiesScrollRef.current.scrollTop;
    if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    scrollDebounceRef.current = setTimeout(() => {
      onStoriesScroll(scrollTop);
    }, 150);
  }, [onStoriesScroll]);

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/api/sessions/${sessionId}/persona-creation-lock`)
      .then(r => setAllowNewPersonaCreation(r.data.allowNewPersonaCreation !== false))
      .catch(() => {});
  }, [sessionId]);

  // Broadcast active persona changes to second screen
  useEffect(() => {
    onActivePersonaChange?.(activeTab);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLock = async () => {
    if (!sessionId || lockLoading) return;
    const next = !allowNewPersonaCreation;
    setLockLoading(true);
    try {
      await api.put(`/api/sessions/${sessionId}/persona-creation-lock`, { allowNewPersonaCreation: next });
      setAllowNewPersonaCreation(next);
    } finally {
      setLockLoading(false);
    }
  };

  const handleAddPersona = async () => {
    if (!sessionId || !newPersonaName.trim() || !newPersonaRole.trim()) return;
    setAddPersonaSaving(true);
    try {
      await api.post(`/api/sessions/${sessionId}/personas`, {
        name: newPersonaName.trim(),
        role: newPersonaRole.trim(),
        goals: newPersonaGoals.split('\n').map(s => s.trim()).filter(Boolean),
        painPoints: newPersonaPainPoints.split('\n').map(s => s.trim()).filter(Boolean),
      });
      setShowAddPersona(false);
      setNewPersonaName(''); setNewPersonaRole(''); setNewPersonaGoals(''); setNewPersonaPainPoints('');
      onRefresh();
    } finally {
      setAddPersonaSaving(false);
    }
  };

  const handleAddStory = async () => {
    if (!sessionId || !newStoryText.trim() || !addStoryForPersona) return;
    setAddStorySaving(true);
    try {
      await api.post(`/api/sessions/${sessionId}/user-stories`, {
        persona: addStoryForPersona,
        story: newStoryText.trim(),
        acceptanceCriteria: newStoryCriteria.split('\n').map(s => s.trim()).filter(Boolean),
        priority: newStoryPriority,
      });
      setAddStoryForPersona(null);
      setNewStoryText(''); setNewStoryCriteria(''); setNewStoryPriority('medium');
      await onReloadData?.();
    } finally {
      setAddStorySaving(false);
    }
  };

  const handleMerge = async (targetPersonaId: string) => {
    if (!sessionId || !mergeSourceId || mergeSourceId === targetPersonaId) return;
    try {
      await api.post(`/api/sessions/${sessionId}/personas/merge`, {
        sourcePersonaId: mergeSourceId,
        targetPersonaId,
      });
      setMergeSourceId(null);
      setActiveTab(targetPersonaId);
      onRefresh();
    } catch {
      setMergeSourceId(null);
    }
  };

  const handleReExtractStories = async (personaId: string, personaName: string) => {
    if (!sessionId || extractingStoriesFor) return;
    setExtractingStoriesFor(personaId);
    try {
      // Snapshot current story count for this persona before triggering
      const encodedPersona = encodeURIComponent(personaName);
      const beforeRes = await api.get(`/api/sessions/${sessionId}/user-stories/persona/${encodedPersona}`);
      const beforeCount = (beforeRes.data.userStories || []).length;

      await api.post(`/api/sessions/${sessionId}/personas/${personaId}/extract-stories`, { personaName });

      // Poll until story count increases or timeout (3 min)
      const deadline = Date.now() + 180000;
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/api/sessions/${sessionId}/user-stories/persona/${encodedPersona}`);
          const afterCount = (res.data.userStories || []).length;
          if (afterCount > beforeCount || Date.now() > deadline) {
            clearInterval(poll);
            setExtractingStoriesFor(null);
            if (onReloadData) await onReloadData();
            else onRefresh();
          }
        } catch {
          if (Date.now() > deadline) {
            clearInterval(poll);
            setExtractingStoriesFor(null);
          }
        }
      }, 5000);
    } catch {
      setExtractingStoriesFor(null);
    }
  };

  const personas = (requirements?.personas.filter(p => !removedPersonaIds.has(p.id)) ?? [])
    .map(p => localPersonaDescriptions.has(p.id) ? { ...p, description: localPersonaDescriptions.get(p.id) } : p);
  const stories = requirements?.userStories.filter(s => !removedRequirementIds.has(s.id)) ?? [];

  // Everything in this session a RAID item can be linked to, plus the other
  // RAID items so blocking chains can be expressed.
  const raidCandidates = useMemo<LinkCandidate[]>(() => {
    const out: LinkCandidate[] = [];
    if (!sessionId) return out;
    for (const s of stories) {
      out.push({
        targetType: 'user_story', targetId: s.id, targetSessionId: sessionId,
        targetPersona: (s as any).persona,
        label: storyText(s as any), sublabel: (s as any).persona,
      });
    }
    for (const n of (requirements?.nonFunctionalRequirements ?? [])) {
      out.push({ targetType: 'nfr', targetId: n.id, targetSessionId: sessionId, label: n.title, sublabel: n.category });
    }
    for (const f of features ?? []) {
      out.push({ targetType: 'feature', targetId: f.id, targetSessionId: sessionId, label: f.name });
    }
    for (const a of adrs ?? []) {
      out.push({ targetType: 'adr', targetId: a.id, targetSessionId: sessionId, label: a.title, sublabel: a.status });
    }
    for (const r of raidItems) {
      out.push({ targetType: 'raid_item', targetId: r.raidId, label: r.title, sublabel: r.type });
    }
    return out;
  }, [sessionId, stories, requirements?.nonFunctionalRequirements, features, adrs, raidItems]);

  /** Tag one story to a phase. null clears it. */
  const handleStoryMilestone = useCallback(async (storyId: string, milestoneId: string | null) => {
    if (!sessionId) return;
    try {
      await api.patch(`/api/sessions/${sessionId}/user-stories/${storyId}`, { milestoneId });
      onReloadData?.();
    } catch {
      toast.error('Could not set the delivery phase');
    }
  }, [sessionId, onReloadData]);

  /**
   * Tag one feature to a phase. No cascade — if the feature has stories we
   * offer to tag them as a separate, explicit action rather than moving them
   * silently.
   */
  const handleFeatureMilestone = useCallback(async (feature: Feature, milestoneId: string | null) => {
    if (!sessionId) return;
    try {
      await updateFeature(sessionId, feature.id, { milestoneId: milestoneId ?? undefined });
      onReloadData?.();
      if (milestoneId) {
        const tagged = stories.filter(s => (s as any).featureIds?.includes(feature.id));
        if (tagged.length > 0) setBulkTagPrompt({ feature, milestoneId });
      }
    } catch {
      toast.error('Could not set the delivery phase');
    }
  }, [sessionId, onReloadData, stories]);

  const confirmBulkTag = useCallback(async () => {
    if (!bulkTagPrompt || !projectId || !sessionId) return;
    const storyIds = stories
      .filter(s => (s as any).featureIds?.includes(bulkTagPrompt.feature.id))
      .map(s => s.id);
    try {
      const { assigned, failures } = await milestoneMutations.assign.mutateAsync({
        milestoneId: bulkTagPrompt.milestoneId, storyIds, sessionId,
      });
      if (failures.length > 0) {
        toast.warning(`Tagged ${assigned}, ${failures.length} failed`);
      } else {
        toast.success(`Tagged ${assigned} stor${assigned === 1 ? 'y' : 'ies'}`);
      }
      onReloadData?.();
    } catch {
      toast.error('Could not tag the stories');
    } finally {
      setBulkTagPrompt(null);
    }
  }, [bulkTagPrompt, projectId, sessionId, stories, milestoneMutations.assign, onReloadData]);

  /**
   * The RAID control bundle for any artefact. Stories, NFRs, features and ADRs
   * all attach the same way; only the target type differs.
   */
  const raidControlsFor = useCallback((targetType: RaidLinkTargetType, targetId: string) => {
    if (!sessionId) return undefined;
    const entry = lookupRaid(raidIndex, targetType, targetId, sessionId);
    const key = `${targetType}#${sessionId}#${targetId}`;
    return {
      items: entry.items,
      openCount: entry.openCount,
      blockingCount: entry.blockingCount,
      isExpanded: expandedRaidTargets.has(key),
      onToggle: () => toggleRaidTarget(key),
      onAdd: () => setRaidModal({
        mode: 'create' as const,
        links: [{ targetType, targetId, targetSessionId: sessionId }],
      }),
      onEditItem: (item: RaidItem) => setRaidModal({ mode: 'edit' as const, item }),
      onResolveItem: handleResolveRaid,
      saving: raidMutations.resolve.isPending,
    };
  }, [sessionId, raidIndex, expandedRaidTargets, toggleRaidTarget, handleResolveRaid, raidMutations.resolve.isPending]);

  const raidControlsForStory = useCallback(
    (storyId: string) => raidControlsFor('user_story', storyId),
    [raidControlsFor]
  );


  // Sort: new first, then by priority
  const sortedStories = [...stories].sort((a, b) => {
    if (a.isNew && !b.isNew) return -1;
    if (!a.isNew && b.isNew) return 1;
    const po: Record<string, number> = { high: 0, medium: 1, low: 2 };
    return (po[a.priority] || 2) - (po[b.priority] || 2);
  });

  const sortedPersonas = [...personas].sort((a, b) => {
    if (a.isNew && !b.isNew) return -1;
    if (!a.isNew && b.isNew) return 1;
    return 0;
  });

  const isSessionContextTab = activeTab === 'session-context';
  const isUnassignedTab = activeTab === 'unassigned';
  const isNFRTab = activeTab === 'nfrs';
  const isFeaturesTab = activeTab === 'features';
  const isAdrTab = activeTab === 'adrs';
  const activePersona = (isSessionContextTab || isUnassignedTab || isNFRTab || isFeaturesTab || isAdrTab) ? null : (sortedPersonas.find(p => p.id === activeTab) ?? sortedPersonas[0] ?? null);

  // Stats
  const newPersonas = personas.filter(p => p.isNew).length;
  const newStories = stories.filter(s => s.isNew).length;
  const highPriority = stories.filter(s => s.priority === 'high').length;
  const newItems = newPersonas + newStories;

  const storiesForPersona = (personaName: string) => {
    const pName = personaName.toLowerCase().trim();
    return sortedStories.filter(story => {
      const s = story as any;
      const raw = story.persona || s.as_a || '';
      const sPersona = String(raw).toLowerCase().trim();
      if (!sPersona) return false;
      return sPersona === pName || sPersona.includes(pName) || pName.includes(sPersona);
    });
  };

  const assignedStoryIds = new Set(
    sortedPersonas.flatMap(p => storiesForPersona(p.name).map(s => s.id))
  );
  const unassignedStories = sortedStories.filter(s => !assignedStoryIds.has(s.id));

  const newStoryPriorityButtonClasses = (p: StoryPriority, selected: boolean) => {
    if (!selected) return 'border-slate-200 text-slate-400 hover:bg-slate-50';
    switch (p) {
      case 'high': return 'bg-red-100 border-red-300 text-red-700';
      case 'medium': return 'bg-amber-100 border-amber-300 text-amber-700';
      default: return 'bg-slate-100 border-slate-300 text-slate-600';
    }
  };

  const deleteConfirmNoun = (type: DeleteConfirmType) => {
    switch (type) {
      case 'persona': return 'persona';
      case 'nfr': return 'NFR';
      case 'feature': return 'feature';
      case 'adr': return 'ADR';
      default: return 'user story';
    }
  };

  const deleteConfirmMessage = (type: DeleteConfirmType) => {
    switch (type) {
      case 'persona': return 'This will hide the persona from this view. It can be recovered by refreshing requirements.';
      case 'nfr': return 'This will permanently delete this NFR.';
      case 'feature': return 'This will permanently delete this feature. Tags on existing NFRs/stories will remain but no longer resolve.';
      case 'adr': return 'This will permanently delete this ADR.';
      default: return 'This will hide the story from this view. It can be recovered by refreshing requirements.';
    }
  };

  const toggleStory = (id: string) => {
    setExpandedStoryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const descriptionText = (d: any) =>
    typeof d === 'string' ? d : Object.values(d as Record<string, string>).join(' ');

  let lockIcon: React.ReactNode;
  if (lockLoading) lockIcon = <Loader2 size={15} className="animate-spin" />;
  else if (allowNewPersonaCreation) lockIcon = <Unlock size={15} />;
  else lockIcon = <Lock size={15} />;

  // Empty state
  if (!requirements && !loading) {
    return (
      <div className="flex-1 overflow-y-auto p-8">
        <div className="w-full flex flex-col gap-6">
          <header className="flex items-center justify-between border-b border-slate-200 pb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <ClipboardList size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-navy">Requirements & Personas</h1>
                <p className="text-slate-500 text-sm font-medium">AI-extracted from session transcript</p>
              </div>
            </div>
            {hasTranscript && (
              <button
                onClick={onRefresh}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
              >
                <RefreshCw size={18} />
                <span>Extract Now</span>
              </button>
            )}
          </header>
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <ClipboardList size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">
              No requirements extracted yet. Requirements will be automatically extracted every 5 minutes.
            </p>
            {hasTranscript && (
              <p className="text-slate-400 text-xs mt-2">Or click <strong>Extract Now</strong> to generate immediately.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={storiesScrollRef} onScroll={handleStoriesScroll} className="flex-1 overflow-y-auto p-8">
      <div className="w-full flex flex-col gap-6">

        {/* Header */}
        <header className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <ClipboardList size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Requirements & Personas</h1>
              <p className="text-slate-500 text-sm font-medium">AI-extracted from session transcript</p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto flex-wrap">
            {requirements && (
              <span className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-navy text-sm font-bold shadow-sm whitespace-nowrap ml-auto md:ml-0">
                {new Date(requirements.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {sessionId && (
              <button
                onClick={toggleLock}
                disabled={lockLoading}
                title={allowNewPersonaCreation ? 'AI can create new personas — click to lock' : 'AI locked to existing personas — click to unlock'}
                className={`flex items-center gap-1.5 rounded-xl h-10 px-4 border text-sm font-bold transition-all disabled:opacity-50 ${
                  allowNewPersonaCreation
                    ? 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                    : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                }`}
              >
                {lockIcon}
                <span className="hidden sm:inline">{allowNewPersonaCreation ? 'Personas: Open' : 'Personas: Locked'}</span>
              </button>
            )}
            {sessionId && (
              <button
                onClick={() => setShowAddPersona(true)}
                className="flex items-center gap-1.5 rounded-xl h-10 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
              >
                <Plus size={15} />
                <span className="hidden sm:inline">Add Persona</span>
              </button>
            )}
            {onClearAll && requirements && (
              <button
                onClick={() => setShowClearConfirm(true)}
                disabled={loading || clearing}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 border border-red-200 bg-transparent hover:bg-red-50 text-red-600 text-sm font-bold transition-all disabled:opacity-50"
              >
                <Trash2 size={18} />
                <span>Clear All</span>
              </button>
            )}
            {onPushToScreen && (
              <button
                onClick={onPushToScreen}
                disabled={!requirements}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 border border-teal-300 bg-teal-50 hover:bg-teal-100 text-teal-700 text-sm font-bold transition-all disabled:opacity-50"
                title="Mirror personas & stories on second screen"
              >
                <MonitorPlay size={18} />
                <span>Push to Screen</span>
              </button>
            )}
            {onGenerateImage && requirements && (
              <button
                onClick={() => setShowImageModal(true)}
                disabled={imageGenLoading}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 border border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-700 text-sm font-bold transition-all disabled:opacity-50"
                title="Generate an AI image from session context"
              >
                {imageGenLoading && !generatingImageFor ? (
                  <><Loader2 size={18} className="animate-spin" /><span>Generating{imageGenCountdown > 0 ? ` ${imageGenCountdown}s` : '…'}</span></>
                ) : (
                  <><ImageIcon size={18} /><span>Generate Image</span></>
                )}
              </button>
            )}
            <button
              onClick={onRefresh}
              disabled={loading}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
              <span>{loading ? 'Loading...' : 'Refresh'}</span>
            </button>
            {onOpenDeepDive && requirements && requirements.personas.length > 0 && (
              <button
                onClick={onOpenDeepDive}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-navy hover:bg-navy/90 transition-colors text-white text-sm font-bold shadow-sm"
                title="Open Story Deep Dive — refine and verify stories with AI agents"
              >
                <Sparkles size={18} />
                <span>Deep Dive</span>
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        </header>

        {/* Loading state */}
        {(() => {
          if (loading && !requirements) {
            return (
              <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
                <Loader2 size={32} className="text-primary animate-spin mx-auto mb-4" />
                <p className="text-slate-500 text-sm font-medium">Extracting requirements from session transcript...</p>
              </div>
            );
          }
          if (requirements) {
            return (
          <>
            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex flex-col gap-1 relative overflow-hidden group">
                <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Users size={48} className="text-purple-500" />
                </div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wide">Personas</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-navy">{personas.length}</p>
                  {newPersonas > 0 && (
                    <span className="text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded text-xs font-bold">+{newPersonas}</span>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex flex-col gap-1 relative overflow-hidden group">
                <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <BookOpen size={48} className="text-blue-500" />
                </div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wide">User Stories</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-navy">{stories.length}</p>
                  {newStories > 0 && (
                    <span className="text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded text-xs font-bold">+{newStories}</span>
                  )}
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex flex-col gap-1 relative overflow-hidden group">
                <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <AlertTriangle size={48} className="text-red-500" />
                </div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wide">High Priority</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-navy">{highPriority}</p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 flex flex-col gap-1 relative overflow-hidden group">
                <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Sparkles size={48} className="text-primary" />
                </div>
                <p className="text-slate-500 text-sm font-medium uppercase tracking-wide">New Items</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-navy">{newItems}</p>
                  {newItems > 0 && (
                    <span className="text-primary bg-primary/10 px-1.5 py-0.5 rounded text-xs font-bold">+{newItems}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Challenge Areas */}
            {challengeAreas.length > 0 && (
              <ChallengeAreasSection
  challengeAreas={challengeAreas}
  generatingImageFor={generatingImageFor}
  onGenerateAllImage={onGenerateImage ? handleGenerateAllChallengeAreasImage : undefined}
  onGenerateAreaImage={onGenerateImage ? handleGenerateChallengeAreaImage : undefined}
/>
            )}

            {/* Personas — Sidebar + Detail */}
            {sortedPersonas.length > 0 && (
              <div className="flex gap-6 items-start">

                {/* Sidebar */}
                <div className="w-72 flex-shrink-0 sticky top-4 max-h-[calc(100vh-160px)] flex flex-col bg-white border border-[#e2e8f0] rounded-xl shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#e2e8f0] bg-slate-50 flex items-center gap-2 flex-shrink-0">
                    <Users size={13} className="text-slate-400" />
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Personas</span>
                    <span className="text-xs text-slate-400">({sortedPersonas.length})</span>
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {sortedPersonas.map(persona => {
                      const initials = persona.name.split(' ').map((w: string) => w[0]).join('').substring(0, 2).toUpperCase();
                      const isActive = !isSessionContextTab && persona.id === activePersona?.id;
                      const pCount = storiesForPersona(persona.name).length;
                      return (
                        <button
                          key={persona.id}
                          onClick={() => setActiveTab(persona.id)}
                          title={persona.role}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 border-b border-[#e2e8f0] last:border-b-0 text-left transition-colors ${
                            isActive ? 'bg-primary/5' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isActive ? 'bg-purple-100 text-purple-700' : 'bg-slate-200 text-slate-500'}`}>
                            {initials}
                          </div>
                          <span className={`text-sm font-semibold truncate flex-1 min-w-0 ${isActive ? 'text-navy' : 'text-slate-600'}`}>
                            {persona.name}
                          </span>
                          {persona.isNew && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-primary text-white shrink-0">NEW</span>
                          )}
                          {pCount > 0 && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${isActive ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'}`}>
                              {pCount}
                            </span>
                          )}
                        </button>
                      );
                    })}

                    {/* Session Context nav item */}
                    {((requirements.technicalSpecs?.coreFunctionality?.length ?? 0) > 0 || (requirements.assumptions?.length ?? 0) > 0) && (
                      <>
                        <div className="h-px bg-[#e2e8f0] mx-3 my-1" />
                        <button
                          onClick={() => setActiveTab('session-context')}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                            isSessionContextTab ? 'bg-amber-50' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isSessionContextTab ? 'bg-amber-100 text-amber-600' : 'bg-slate-200 text-slate-500'}`}>
                            <Settings size={13} />
                          </div>
                          <span className={`text-sm font-semibold truncate flex-1 min-w-0 ${isSessionContextTab ? 'text-navy' : 'text-slate-600'}`}>
                            Session Context
                          </span>
                        </button>
                      </>
                    )}

                    {/* NFRs nav item */}
                    <>
                      <div className="h-px bg-[#e2e8f0] mx-3 my-1" />
                      <button
                        onClick={() => setActiveTab('nfrs')}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                          isNFRTab ? 'bg-teal-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isNFRTab ? 'bg-teal-100 text-teal-600' : 'bg-slate-200 text-slate-500'}`}>
                          <ShieldCheck size={13} />
                        </div>
                        <span className={`text-sm font-semibold truncate flex-1 min-w-0 ${isNFRTab ? 'text-navy' : 'text-slate-600'}`}>
                          NFRs
                        </span>
                        {(requirements?.nonFunctionalRequirements?.length ?? 0) > 0 && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${isNFRTab ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'}`}>
                            {requirements?.nonFunctionalRequirements?.length}
                          </span>
                        )}
                      </button>
                    </>

                    {/* Features nav item */}
                    <>
                      <div className="h-px bg-[#e2e8f0] mx-3 my-1" />
                      <button
                        onClick={() => setActiveTab('features')}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                          isFeaturesTab ? 'bg-indigo-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isFeaturesTab ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-200 text-slate-500'}`}>
                          <Layers size={13} />
                        </div>
                        <span className={`text-sm font-semibold truncate flex-1 min-w-0 ${isFeaturesTab ? 'text-navy' : 'text-slate-600'}`}>
                          Features
                        </span>
                        {features.length > 0 && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${isFeaturesTab ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'}`}>
                            {features.length}
                          </span>
                        )}
                      </button>
                    </>

                    {/* ADRs nav item */}
                    <>
                      <div className="h-px bg-[#e2e8f0] mx-3 my-1" />
                      <button
                        onClick={() => setActiveTab('adrs')}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                          isAdrTab ? 'bg-amber-50' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isAdrTab ? 'bg-amber-100 text-amber-600' : 'bg-slate-200 text-slate-500'}`}>
                          <GitBranch size={13} />
                        </div>
                        <span className={`text-sm font-semibold truncate flex-1 min-w-0 ${isAdrTab ? 'text-navy' : 'text-slate-600'}`}>
                          ADRs
                        </span>
                        {adrs.length > 0 && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${isAdrTab ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'}`}>
                            {adrs.length}
                          </span>
                        )}
                      </button>
                    </>

                    {/* Unassigned nav item */}
                    {unassignedStories.length > 0 && (
                      <>
                        <div className="h-px bg-[#e2e8f0] mx-3 my-1" />
                        <button
                          onClick={() => setActiveTab('unassigned')}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                            isUnassignedTab ? 'bg-slate-100' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isUnassignedTab ? 'bg-slate-200 text-slate-600' : 'bg-slate-200 text-slate-500'}`}>
                            <BookOpen size={13} />
                          </div>
                          <span className={`text-sm font-semibold truncate flex-1 min-w-0 ${isUnassignedTab ? 'text-navy' : 'text-slate-600'}`}>
                            Unassigned
                          </span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${isUnassignedTab ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-500'}`}>
                            {unassignedStories.length}
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Right panel */}
                <div className="flex-1 min-w-0">

                  {/* NFRs panel */}
                  {isNFRTab && (
                    <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                      <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                        <ShieldCheck size={15} className="text-teal-500" />
                        <h3 className="text-sm font-bold text-slate-600">Non-Functional Requirements</h3>
                        {(requirements?.nonFunctionalRequirements?.length ?? 0) > 0 && (
                          <span className="text-xs text-slate-400">({requirements?.nonFunctionalRequirements?.length})</span>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                          {sessionId && (
                            <button
                              onClick={() => {
                                setNfrCreateMode(true);
                                setNfrEditTarget({ id: '', category: 'performance', title: '', description: '', priority: 'medium', acceptanceCriteria: [] });
                              }}
                              className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-white hover:bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold transition-colors"
                            >
                              <Plus size={13} /> Add NFR
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (!sessionId || nfrLoading) return;
                              const hasNfrs = (requirements?.nonFunctionalRequirements?.length ?? 0) > 0;
                              if (hasNfrs) { setShowNfrRerunConfirm(true); return; }
                              runNfrExtraction();
                            }}
                            disabled={nfrLoading || !sessionId}
                            className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold shadow-sm disabled:opacity-60"
                          >
                            {nfrLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                            {nfrLoading ? 'Extracting…' : 'Extract NFRs'}
                          </button>
                        </div>
                      </div>

                      {/* NFR bulk action bar */}
                      {selectedNfrIds.size > 0 && (
                        <div className="px-4 py-2.5 bg-primary/[0.04] border-b border-primary/20 flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-primary">{selectedNfrIds.size} selected</span>
                          <div className="flex items-center gap-1.5 ml-2 flex-wrap">
                            <button
                              onClick={() => { setBulkNfrAction('merge'); setBulkNfrConfirm(true); }}
                              disabled={selectedNfrIds.size < 2}
                              className="flex items-center gap-1 rounded-lg h-7 px-2.5 bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 transition-colors"
                            >
                              <GitMerge size={12} /> Merge
                            </button>
                            <button
                              onClick={() => { setBulkNfrAction('priority'); setBulkNfrConfirm(true); }}
                              className="flex items-center gap-1 rounded-lg h-7 px-2.5 bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50 transition-colors"
                            >
                              Priority
                            </button>
                            <button
                              onClick={() => { setBulkNfrAction('delete'); setBulkNfrConfirm(true); }}
                              className="flex items-center gap-1 rounded-lg h-7 px-2.5 bg-white border border-red-200 text-red-500 text-xs font-semibold hover:bg-red-50 transition-colors"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                          {bulkNfrConfirm && bulkNfrAction === 'priority' && (
                            <div className="flex items-center gap-2 ml-2">
                              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                                {(['high','medium','low'] as const).map(p => (
                                  <button key={p} type="button" onClick={() => setBulkNfrPriorityValue(p)}
                                    className={`px-2 py-1 text-xs font-semibold capitalize transition-all ${bulkNfrPriorityValue === p ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                                    {p}
                                  </button>
                                ))}
                              </div>
                              <button
                                onClick={async () => { await executeBulkNfrAction(); setBulkNfrConfirm(false); setBulkNfrAction(null); }}
                                disabled={bulkNfrActionLoading}
                                className="rounded-lg h-7 px-3 bg-primary text-white text-xs font-bold hover:bg-primary-dark disabled:opacity-50"
                              >
                                {bulkNfrActionLoading ? 'Saving…' : 'Confirm'}
                              </button>
                            </div>
                          )}
                          {bulkNfrConfirm && bulkNfrAction === 'delete' && (
                            <div className="flex items-center gap-2 ml-2">
                              <span className="text-xs text-red-600 font-semibold">Delete {selectedNfrIds.size} NFRs?</span>
                              <button
                                onClick={async () => { await executeBulkNfrAction(); setBulkNfrConfirm(false); setBulkNfrAction(null); }}
                                disabled={bulkNfrActionLoading}
                                className="rounded-lg h-7 px-3 bg-red-500 text-white text-xs font-bold hover:bg-red-600 disabled:opacity-50"
                              >
                                {bulkNfrActionLoading ? 'Deleting…' : 'Confirm'}
                              </button>
                            </div>
                          )}
                          {bulkNfrConfirm && bulkNfrAction === 'merge' && (
                            <div className="flex items-center gap-2 ml-2">
                              <button
                                onClick={async () => { setBulkNfrConfirm(false); setBulkNfrAction(null); await triggerNfrMerge(); }}
                                disabled={bulkNfrActionLoading}
                                className="flex items-center gap-1 rounded-lg h-7 px-3 bg-primary text-white text-xs font-bold hover:bg-primary-dark disabled:opacity-50"
                              >
                                <GitMerge size={12} /> Preview merge
                              </button>
                            </div>
                          )}
                          <button onClick={clearNfrSelection} className="ml-auto text-[11px] text-slate-400 hover:text-slate-600">Clear</button>
                        </div>
                      )}

                      {(requirements?.nonFunctionalRequirements?.length ?? 0) === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                            <ShieldCheck size={24} className="text-slate-400" />
                          </div>
                          <p className="text-sm font-semibold text-slate-500">No NFRs extracted yet</p>
                          <p className="text-xs text-slate-400 mt-1">Click "Extract NFRs" to analyse the session transcript for non-functional requirements.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {(['performance', 'security', 'scalability', 'availability', 'usability', 'maintainability', 'compliance'] as NFR['category'][]).map(category => {
                            const categoryNFRs = (requirements?.nonFunctionalRequirements ?? []).filter(n => n.category === category);
                            if (categoryNFRs.length === 0) return null;
                            return (
                              <div key={category} className="px-5 py-4">
                                <h4 className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3 capitalize">{category}</h4>
                                <div className="flex flex-col gap-3">
                                  {categoryNFRs.map(nfr => {
                                    let priorityClasses: string;
                                    if (nfr.priority === 'high') priorityClasses = 'bg-red-50 text-red-600 border-red-200';
                                    else if (nfr.priority === 'medium') priorityClasses = 'bg-amber-50 text-amber-600 border-amber-200';
                                    else priorityClasses = 'bg-green-50 text-green-600 border-green-200';
                                    const isNfrSelected = selectedNfrIds.has(nfr.id);
                                    return (
                                      <div key={nfr.id} className={`group border rounded-xl p-4 transition-colors ${isNfrSelected ? 'border-primary/40 bg-primary/[0.02]' : 'border-[#e2e8f0]'}`}>
                                        <div className="flex items-start gap-3">
                                          <input type="checkbox" checked={isNfrSelected} onChange={() => toggleNfrSelection(nfr.id)}
                                            className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer mt-0.5 shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${priorityClasses} capitalize`}>{nfr.priority}</span>
                                              <span className="text-sm font-semibold text-navy">{nfr.title}</span>
                                            </div>
                                            <p className="text-xs text-slate-500 mb-2">{nfr.description}</p>
                                            {nfr.acceptanceCriteria.length > 0 && (
                                              <ul className="flex flex-col gap-1">
                                                {nfr.acceptanceCriteria.map((ac) => (
                                                  <li key={ac} className="flex items-start gap-1.5 text-xs text-slate-500">
                                                    <CheckCircle size={11} className="text-primary shrink-0 mt-0.5" />
                                                    {ac}
                                                  </li>
                                                ))}
                                              </ul>
                                            )}
                                            {(nfr as any).notes?.length > 0 && (
                                              <ul className="flex flex-col gap-1 mt-1.5 pt-1.5 border-t border-slate-100">
                                                {(nfr as any).notes.map((n: string) => (
                                                  <li key={n} className="flex items-start gap-1.5 text-xs text-slate-400 italic">
                                                    <StickyNote size={11} className="text-slate-300 shrink-0 mt-0.5" />
                                                    {n}
                                                  </li>
                                                ))}
                                              </ul>
                                            )}
                                            {(nfr as any).source && (
                                              <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-400">
                                                <BookMarked size={9} className="shrink-0" />
                                                <span className="truncate">{(nfr as any).source}</span>
                                              </p>
                                            )}
                                            <RaidCardSection controls={raidControlsFor('nfr', nfr.id)} />
                                          </div>
                                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                            {sessionId && (
                                              <button
                                                onClick={() => setNfrEditTarget(nfr)}
                                                className="p-1 rounded hover:bg-primary/10 text-slate-400 hover:text-primary transition-colors"
                                                title="Edit NFR"
                                              >
                                                <Pencil size={14} />
                                              </button>
                                            )}
                                            <button
                                              onClick={() => setDeleteConfirm({ type: 'nfr', id: nfr.id, label: nfr.title })}
                                              className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                              title="Delete NFR"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Features panel */}
                  {isFeaturesTab && (
                    <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                      <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                        <Layers size={15} className="text-indigo-500" />
                        <h3 className="text-sm font-bold text-slate-600">Features</h3>
                        {features.length > 0 && (
                          <span className="text-xs text-slate-400">({features.length})</span>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                          {sessionId && (
                            <button
                              onClick={() => {
                                setFeatureCreateMode(true);
                                setFeatureEditTarget({ id: '', sessionId: sessionId || '', name: '', description: '', relatedPersonas: [], status: 'draft' });
                              }}
                              className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-white hover:bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold transition-colors"
                            >
                              <Plus size={13} /> Add Feature
                            </button>
                          )}
                        </div>
                      </div>

                      {features.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                            <Layers size={24} className="text-slate-400" />
                          </div>
                          <p className="text-sm font-semibold text-slate-500">No features yet</p>
                          <p className="text-xs text-slate-400 mt-1">Group NFRs and stories by feature/component — useful for anything that isn't a single persona.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {features.map(feature => (
                            <div key={feature.id} className="group px-5 py-4 flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize ${
                                    feature.status === 'confirmed'
                                      ? 'bg-green-50 text-green-600 border-green-200'
                                      : 'bg-slate-100 text-slate-500 border-slate-200'
                                  }`}>
                                    {feature.status || 'draft'}
                                  </span>
                                  <span className="text-sm font-semibold text-navy">{feature.name}</span>
                                </div>
                                {feature.description && (
                                  <p className="text-xs text-slate-500 mb-2">{feature.description}</p>
                                )}
                                {(feature.relatedPersonas?.length ?? 0) > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {feature.relatedPersonas!.map(p => (
                                      <span key={p} className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 text-[10px] font-semibold">{p}</span>
                                    ))}
                                  </div>
                                )}
                                {projectId && milestones.length > 0 && (
                                  <div className="mt-1.5">
                                    <MilestonePicker
                                      milestones={milestones}
                                      value={(feature as any).milestoneId}
                                      onChange={(id) => handleFeatureMilestone(feature, id)}
                                      size="md"
                                    />
                                  </div>
                                )}
                                {((feature as any).notes?.length ?? 0) > 0 && (
                                  <ul className="flex flex-col gap-1 mt-1.5 pt-1.5 border-t border-slate-100">
                                    {(feature as any).notes.map((n: string) => (
                                      <li key={n} className="flex items-start gap-1.5 text-xs text-slate-400 italic">
                                        <StickyNote size={11} className="text-slate-300 shrink-0 mt-0.5" />
                                        {n}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {(feature as any).source && (
                                  <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-400">
                                    <BookMarked size={9} className="shrink-0" />
                                    <span className="truncate">{(feature as any).source}</span>
                                  </p>
                                )}
                                <RaidCardSection controls={raidControlsFor('feature', feature.id)} />
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                {sessionId && (
                                  <button
                                    onClick={() => setFeatureEditTarget(feature)}
                                    className="p-1 rounded hover:bg-primary/10 text-slate-400 hover:text-primary transition-colors"
                                    title="Edit Feature"
                                  >
                                    <Pencil size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => setDeleteConfirm({ type: 'feature', id: feature.id, label: feature.name })}
                                  className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                  title="Delete Feature"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ADRs panel */}
                  {isAdrTab && (
                    <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                      <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                        <GitBranch size={15} className="text-amber-500" />
                        <h3 className="text-sm font-bold text-slate-600">Architectural Decision Records</h3>
                        {adrs.length > 0 && (
                          <span className="text-xs text-slate-400">({adrs.length})</span>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                          {sessionId && (
                            <button
                              onClick={() => {
                                setAdrCreateMode(true);
                                setAdrEditTarget({ id: '', sessionId: sessionId || '', title: '', status: 'proposed', context: '', decision: '', consequences: '', alternatives: [], featureIds: [] });
                              }}
                              className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-white hover:bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold transition-colors"
                            >
                              <Plus size={13} /> Add ADR
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (!sessionId || adrLoading) return;
                              if (adrs.length > 0) { setShowAdrRerunConfirm(true); return; }
                              runAdrExtraction();
                            }}
                            disabled={adrLoading || !sessionId}
                            className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold shadow-sm disabled:opacity-60"
                          >
                            {adrLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                            {adrLoading ? 'Extracting…' : 'Extract ADRs'}
                          </button>
                        </div>
                      </div>

                      {adrs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                            <GitBranch size={24} className="text-slate-400" />
                          </div>
                          <p className="text-sm font-semibold text-slate-500">No ADRs captured yet</p>
                          <p className="text-xs text-slate-400 mt-1">Click "Extract ADRs" to analyse the session transcript for architectural decisions.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {adrs.map(adr => {
                            let statusClasses: string;
                            if (adr.status === 'accepted') statusClasses = 'bg-green-50 text-green-600 border-green-200';
                            else if (adr.status === 'proposed') statusClasses = 'bg-amber-50 text-amber-600 border-amber-200';
                            else statusClasses = 'bg-slate-100 text-slate-500 border-slate-200';
                            return (
                              <div key={adr.id} className="group px-5 py-4 flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusClasses} capitalize`}>{adr.status}</span>
                                    <span className="text-sm font-semibold text-navy">{adr.title}</span>
                                  </div>
                                  {adr.context && (
                                    <p className="text-xs text-slate-500 mb-1"><span className="font-semibold text-slate-600">Context: </span>{adr.context}</p>
                                  )}
                                  {adr.decision && (
                                    <p className="text-xs text-slate-500 mb-1"><span className="font-semibold text-slate-600">Decision: </span>{adr.decision}</p>
                                  )}
                                  {adr.consequences && (
                                    <p className="text-xs text-slate-500 mb-2"><span className="font-semibold text-slate-600">Consequences: </span>{adr.consequences}</p>
                                  )}
                                  {(adr.alternatives?.length ?? 0) > 0 && (
                                    <ul className="flex flex-col gap-1">
                                      {adr.alternatives!.map((alt) => (
                                        <li key={alt} className="flex items-start gap-1.5 text-xs text-slate-500">
                                          <CheckCircle size={11} className="text-primary shrink-0 mt-0.5" />
                                          {alt}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {((adr as any).notes?.length ?? 0) > 0 && (
                                    <ul className="flex flex-col gap-1 mt-1.5 pt-1.5 border-t border-slate-100">
                                      {(adr as any).notes.map((n: string) => (
                                        <li key={n} className="flex items-start gap-1.5 text-xs text-slate-400 italic">
                                          <StickyNote size={11} className="text-slate-300 shrink-0 mt-0.5" />
                                          {n}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {(adr as any).source && (
                                    <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-400">
                                      <BookMarked size={9} className="shrink-0" />
                                      <span className="truncate">{(adr as any).source}</span>
                                    </p>
                                  )}
                                  <RaidCardSection controls={raidControlsFor('adr', adr.id)} />
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                  {sessionId && (
                                    <button
                                      onClick={() => setAdrEditTarget(adr)}
                                      className="p-1 rounded hover:bg-primary/10 text-slate-400 hover:text-primary transition-colors"
                                      title="Edit ADR"
                                    >
                                      <Pencil size={14} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => setDeleteConfirm({ type: 'adr', id: adr.id, label: adr.title })}
                                    className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                    title="Delete ADR"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Unassigned stories panel */}
                  {isUnassignedTab && (
                    <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                      <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                        <BookOpen size={15} className="text-slate-400" />
                        <h3 className="text-sm font-bold text-slate-600">Unassigned Stories</h3>
                        <span className="text-xs text-slate-400">({unassignedStories.length})</span>
                      </div>
                      <table className="w-full text-sm">
                        <StoryTableHead columns={STORY_COLUMNS.unassigned} />
                        <tbody className="divide-y divide-slate-100">
                          {unassignedStories.map(story => (
                            <StoryRow
                              key={story.id}
                              story={story as any}
                              columns={STORY_COLUMNS.unassigned}
                              isExpanded={expandedStoryIds.has(story.id)}
                              isSelected={selectedStoryIds.has(story.id)}
                              onToggleExpand={toggleStory}
                              onToggleSelect={toggleStorySelection}
                              onEdit={sessionId ? (s) => setStoryEditTarget(s as any) : undefined}
                              onDelete={(s) => setDeleteConfirm({ type: 'story', id: s.id, label: storyText(s).substring(0, 80) })}
                              onToggleLock={sessionId ? (s) => handleToggleStoryLock(s as any) : undefined}
                              onAvatarReview={sessionId ? (s) => triggerAvatarReview({ type: 'story', storyId: s.id, persona: (s as any).persona }) : undefined}
                              raid={raidControlsForStory(story.id)}
                              milestone={projectId && milestones.length > 0 ? {
                                milestones,
                                onChange: (id) => handleStoryMilestone(story.id, id),
                              } : undefined}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Session Context panel */}
                  {isSessionContextTab && (
                    <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                      <div className="px-5 py-3.5 border-b border-[#e2e8f0] bg-slate-50 flex items-center gap-2">
                        <Settings size={15} className="text-amber-500" />
                        <h3 className="text-sm font-bold text-slate-600">Session Context</h3>
                        <span className="text-xs text-slate-400">Specs &amp; assumptions</span>
                      </div>
                      <div className="p-6 flex flex-col gap-6">
                        {(requirements.technicalSpecs?.coreFunctionality?.length ?? 0) > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <Settings size={16} className="text-slate-400" />
                              <h3 className="text-sm font-bold text-navy">Technical Specs</h3>
                              <span className="text-xs text-slate-400">({requirements.technicalSpecs.coreFunctionality.length} items)</span>
                            </div>
                            <ul className="space-y-2">
                              {requirements.technicalSpecs.coreFunctionality.map((item) => (
                                <li key={typeof item === 'string' ? item : ((item as any).description || (item as any).name || JSON.stringify(item))} className="flex items-start gap-3 text-sm text-slate-600 bg-slate-50 rounded-lg px-4 py-2.5">
                                  <CheckCircle size={14} className="text-primary mt-0.5 shrink-0" />
                                  <span>{typeof item === 'string' ? item : (item as any).description || (item as any).name || JSON.stringify(item)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(requirements.assumptions?.length ?? 0) > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <Lightbulb size={16} className="text-amber-500" />
                              <h3 className="text-sm font-bold text-navy">Session Assumptions</h3>
                              <span className="text-xs text-slate-400">({requirements.assumptions.length})</span>
                            </div>
                            <ul className="space-y-2">
                              {requirements.assumptions.map((item) => (
                                <li key={typeof item === 'string' ? item : ((item as any).description || (item as any).assumption || JSON.stringify(item))} className="flex items-start gap-3 text-sm text-slate-600 bg-slate-50 rounded-lg px-4 py-2.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                                  <span>{typeof item === 'string' ? item : (item as any).description || (item as any).assumption || JSON.stringify(item)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Persona detail panel */}
                  {activePersona && (() => {
                    const initials = activePersona.name.split(' ').map((w: string) => w[0]).join('').substring(0, 2).toUpperCase();
                    const pStories = (() => {
                      const po: Record<string, number> = { high: 0, medium: 1, low: 2 };
                      let s = storiesForPersona(activePersona.name);
                      if (storySearch.trim()) {
                        const q = storySearch.toLowerCase();
                        s = s.filter(st => storyText(st as any).toLowerCase().includes(q) || st.id.toLowerCase().includes(q));
                      }
                      if (storyPriorityFilter !== 'all') {
                        s = s.filter(st => st.priority === storyPriorityFilter);
                      }
                      if (storySortBy) {
                        s = [...s].sort((a, b) => {
                          let cmp = 0;
                          if (storySortBy === 'id') cmp = a.id.localeCompare(b.id);
                          else if (storySortBy === 'priority') cmp = (po[a.priority] || 2) - (po[b.priority] || 2);
                          else if (storySortBy === 'story') cmp = storyText(a as any).localeCompare(storyText(b as any));
                          return storySortDir === 'asc' ? cmp : -cmp;
                        });
                      }
                      return s;
                    })();
                    return (
                      <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-hidden">
                        <div className="p-6 flex flex-col gap-6">

                          {/* Persona detail header */}
                          <div className="flex flex-col gap-3">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-base shrink-0">
                                  {initials}
                                </div>
                                <div>
                                  <h2 className="text-lg font-bold text-navy leading-tight">{activePersona.name}</h2>
                                  <p className="text-sm text-slate-500">{activePersona.role}</p>
                                  {activePersona.firstSeen && (
                                    <p className="text-xs text-slate-400 mt-0.5">
                                      First seen: {new Date(activePersona.firstSeen).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => setDeleteConfirm({ type: 'persona', id: activePersona.id, label: activePersona.name })}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors shrink-0"
                                title="Remove persona"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {onStartOpenDesignTask && (
                                <div className="relative">
                                  <button
                                    onClick={() => setOpenDesignPickerTarget(p => p === `persona-${activePersona.id}` ? null : `persona-${activePersona.id}`)}
                                    disabled={openDesigningStoryId === `persona-${activePersona.id}`}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 text-xs font-semibold transition-colors disabled:opacity-50"
                                  >
                                    {openDesigningStoryId === `persona-${activePersona.id}` ? <Loader2 size={14} className="animate-spin" /> : <Paintbrush size={14} />}
                                    Open Design
                                    <ChevronDown size={12} />
                                  </button>
                                  {openDesignPickerTarget === `persona-${activePersona.id}` && (
                                    <div className="absolute top-full left-0 mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[160px]">
                                      {([
                                        { label: 'Prototype', skillId: null },
                                        { label: 'Deck', skillId: 'slides' },
                                        { label: 'Design System', skillId: 'design-brief' },
                                      ] as { label: string; skillId: string | null }[]).map(({ label, skillId }) => (
                                        <button
                                          key={label}
                                          onClick={async () => {
                                            setOpenDesignPickerTarget(null);
                                            const personaStories = storiesForPersona(activePersona.name);
                                            const personaStoryTexts = personaStories.map(s => storyText(s as any));
                                            const parts = [
                                              `Persona: ${activePersona.name} (${activePersona.role})`,
                                              activePersona.description ? `About: ${descriptionText(activePersona.description)}` : '',
                                              activePersona.goals.length > 0 ? `Goals: ${activePersona.goals.join('; ')}` : '',
                                              activePersona.painPoints.length > 0 ? `Pain points: ${activePersona.painPoints.join('; ')}` : '',
                                              personaStories.length > 0 ? `User stories: ${personaStoryTexts.join(' | ')}` : '',
                                            ].filter(Boolean);
                                            setOpenDesigningStoryId(`persona-${activePersona.id}`);
                                            try { await onStartOpenDesignTask(parts.join('\n'), skillId); } finally { setOpenDesigningStoryId(null); }
                                          }}
                                          className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              {onGenerateImage && (
                                <button
                                  onClick={() => handleGeneratePersonaImage({
                                    id: activePersona.id,
                                    name: activePersona.name,
                                    role: activePersona.role,
                                    goals: activePersona.goals,
                                    painPoints: activePersona.painPoints,
                                  })}
                                  disabled={!!generatingImageFor}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-orange-200 text-orange-600 hover:bg-orange-50 text-xs font-semibold transition-colors disabled:opacity-50"
                                >
                                  {generatingImageFor === `persona-${activePersona.id}` ? (
                                    <><Loader2 size={14} className="animate-spin" />{imageGenCountdown > 0 ? `${imageGenCountdown}s` : '…'}</>
                                  ) : (
                                    <><ImageIcon size={14} />Generate Image</>
                                  )}
                                </button>
                              )}
                              {projectId && sessionId && (
                                <button
                                  onClick={() => setMakeAvatarPersona({
                                    personaId: activePersona.id,
                                    name: activePersona.name,
                                    role: activePersona.role,
                                    goals: activePersona.goals,
                                    painPoints: activePersona.painPoints,
                                  })}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-200 text-teal-600 hover:bg-teal-50 text-xs font-semibold transition-colors"
                                >
                                  <Bot size={14} />
                                  Make Avatar
                                </button>
                              )}
                              {sessionId && sortedPersonas.length > 1 && (
                                <button
                                  onClick={() => setMergeSourceId(mergeSourceId === activePersona.id ? null : activePersona.id)}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                                    mergeSourceId === activePersona.id
                                      ? 'border-orange-300 bg-orange-50 text-orange-600'
                                      : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                                  }`}
                                  title="Merge this persona into another"
                                >
                                  <GitMerge size={14} />
                                  {mergeSourceId === activePersona.id ? 'Cancel Merge' : 'Merge'}
                                </button>
                              )}
                              {sessionId && (
                                <button
                                  onClick={() => setAddStoryForPersona(activePersona.name)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary text-xs font-semibold transition-colors"
                                >
                                  <Plus size={14} />
                                  Add Story
                                </button>
                              )}
                              {sessionId && (
                                <button
                                  onClick={() => handleReExtractStories(activePersona.id, activePersona.name)}
                                  disabled={!!extractingStoriesFor}
                                  title="Re-extract stories from full transcript for this persona"
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700 text-xs font-semibold transition-colors disabled:opacity-50"
                                >
                                  {extractingStoriesFor === activePersona.id
                                    ? <><Loader2 size={14} className="animate-spin" />Extracting…</>
                                    : <><Wand2 size={14} />Re-extract Stories</>
                                  }
                                </button>
                              )}
                              {sessionId && (
                                <button
                                  onClick={() => setReviewModalPersonaId(activePersona.id)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary text-xs font-semibold transition-colors"
                                  title="Review stories with AI agent"
                                >
                                  <MessageSquare size={14} />
                                  Review Stories
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Merge target picker */}
                          {mergeSourceId === activePersona.id && (
                            <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 flex flex-col gap-3">
                              <p className="text-sm font-bold text-orange-700 flex items-center gap-2">
                                <GitMerge size={15} />
                                Merge <span className="font-extrabold">{activePersona.name}</span> into…
                              </p>
                              <p className="text-xs text-orange-600">Goals and pain points will be combined. All stories will be reassigned. This persona will be deleted.</p>
                              <div className="flex flex-wrap gap-2">
                                {sortedPersonas.filter(p => p.id !== activePersona.id).map(p => (
                                  <button
                                    key={p.id}
                                    onClick={() => handleMerge(p.id)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-orange-200 hover:bg-orange-100 text-orange-700 text-xs font-semibold transition-colors"
                                  >
                                    <GitMerge size={12} />
                                    {p.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* 3-column info */}
                          <div>
                          <button
                            onClick={() => setPersonaInfoCollapsed(c => !c)}
                            className="w-full flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3 hover:text-slate-600 transition-colors group"
                          >
                            <span className="flex items-center gap-1.5">About, Goals & Pain Points</span>
                            {personaInfoCollapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
                          </button>
                          {!personaInfoCollapsed && <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div className="bg-slate-50 rounded-xl p-4">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                                About
                                {sessionId && editingAboutPersonaId !== activePersona.id && (
                                  <button
                                    onClick={() => { setEditingAboutPersonaId(activePersona.id); setEditAboutDraft(activePersona.description || ''); }}
                                    className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                                    title="Edit about"
                                  >
                                    <Pencil size={11} />
                                  </button>
                                )}
                              </p>
                              {(() => {
                                if (editingAboutPersonaId === activePersona.id) {
                                  return (
                                <div className="flex flex-col gap-2">
                                  <textarea
                                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                                    rows={4}
                                    value={editAboutDraft}
                                    onChange={e => setEditAboutDraft(e.target.value)}
                                    autoFocus
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={async () => {
                                        if (sessionId) {
                                          await api.patch(`/api/sessions/${sessionId}/personas/${activePersona.id}`, { description: editAboutDraft, name: activePersona.name, role: activePersona.role });
                                          setLocalPersonaDescriptions(prev => new Map(prev).set(activePersona.id, editAboutDraft));
                                        }
                                        setEditingAboutPersonaId(null);
                                      }}
                                      className="flex items-center justify-center rounded-lg h-7 px-3 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingAboutPersonaId(null)}
                                      className="flex items-center justify-center rounded-lg h-7 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                                  );
                                }
                                if (activePersona.description) {
                                  return <p className="text-sm text-slate-700 leading-relaxed">{descriptionText(activePersona.description)}</p>;
                                }
                                return <p className="text-sm text-slate-400 italic">No description available.</p>;
                              })()}
                            </div>
                            <div className="bg-slate-50 rounded-xl p-4">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Goals</p>
                              {activePersona.goals.length > 0 ? (
                                <ul className="space-y-1.5">
                                  {activePersona.goals.map((goal) => (
                                    <li key={goal} className="flex items-start gap-2 text-sm text-slate-700">
                                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                                      {goal}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-slate-400 italic">No goals listed.</p>
                              )}
                            </div>
                            <div className="bg-slate-50 rounded-xl p-4">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Pain Points</p>
                              {activePersona.painPoints.length > 0 ? (
                                <ul className="space-y-1.5">
                                  {activePersona.painPoints.map((pain) => (
                                    <li key={pain} className="flex items-start gap-2 text-sm text-slate-700">
                                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                                      {pain}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-slate-400 italic">No pain points listed.</p>
                              )}
                            </div>
                          </div>}
                          </div>

                          {/* User stories */}
                          <div>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                              <BookOpen size={13} />
                              User Stories
                              <span className="font-normal normal-case text-slate-400">({pStories.length})</span>
                            </p>
                            {pStories.length === 0 ? (
                              <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center">
                                <p className="text-slate-400 text-sm">No user stories linked to this persona yet.</p>
                              </div>
                            ) : (
                              <div className="rounded-xl border border-slate-200 overflow-hidden">
                                {/* Bulk action bar */}
                                {selectedStoryIds.size > 0 && (
                                  <div className="px-4 py-2.5 bg-primary/5 border-b border-primary/20 flex items-center gap-3 flex-wrap">
                                    <span className="text-xs font-bold text-primary">{selectedStoryIds.size} selected</span>
                                    {!bulkStoryConfirm ? (
                                      <>
                                        <button onClick={triggerStoryMerge} disabled={selectedStoryIds.size < 2}
                                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors">
                                          <GitMerge size={12} />Merge
                                        </button>
                                        <button onClick={() => triggerAvatarReview({ type: 'selection', storyIds: Array.from(selectedStoryIds), persona: activePersona.name })}
                                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                                          <Sparkles size={12} />Review with avatars
                                        </button>
                                        <button onClick={() => { setBulkStoryAction('priority'); setBulkStoryConfirm(true); }}
                                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                                          Priority
                                        </button>
                                        <button onClick={() => { setBulkStoryAction('move'); setBulkMovePersona(sortedPersonas[0]?.name || ''); setBulkStoryConfirm(true); }}
                                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                                          Move to Persona
                                        </button>
                                        <button onClick={() => { setBulkStoryAction('delete'); setBulkStoryConfirm(true); }}
                                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors">
                                          <Trash2 size={12} />Delete
                                        </button>
                                        <button onClick={clearStorySelection}
                                          className="ml-auto flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors">
                                          <X size={12} />Clear
                                        </button>
                                      </>
                                    ) : (
                                      <div className="flex items-center gap-2 flex-wrap">
                                        {bulkStoryAction === 'priority' && (
                                          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                                            {(['high', 'medium', 'low'] as const).map(p => (
                                              <button key={p} onClick={() => setBulkPriorityValue(p)}
                                                className={`px-2.5 py-1 text-xs font-semibold transition-all capitalize ${bulkPriorityValue === p ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                                                {p}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                        {bulkStoryAction === 'move' && (
                                          <select value={bulkMovePersona} onChange={e => setBulkMovePersona(e.target.value)}
                                            className="border border-slate-200 rounded-lg px-2 py-1 text-xs text-navy bg-white focus:ring-1 focus:ring-primary outline-none">
                                            {sortedPersonas.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                                          </select>
                                        )}
                                        {bulkStoryAction === 'delete' && (
                                          <span className="text-xs text-red-600 font-medium">Delete {selectedStoryIds.size} stories?</span>
                                        )}
                                        <button onClick={executeBulkStoryAction} disabled={bulkActionLoading}
                                          className="px-3 py-1 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary-dark disabled:opacity-60 transition-colors">
                                          {bulkActionLoading ? 'Working…' : 'Confirm'}
                                        </button>
                                        <button onClick={() => { setBulkStoryConfirm(false); setBulkStoryAction(null); }}
                                          className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs font-semibold text-slate-500 hover:bg-slate-50 transition-colors">
                                          Cancel
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* Search + filter bar */}
                                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 flex-wrap">
                                  <div className="relative">
                                    <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                    <input
                                      value={storySearch}
                                      onChange={e => setStorySearch(e.target.value)}
                                      placeholder="Search stories…"
                                      className="pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg w-48 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                    />
                                  </div>
                                  <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                                    {(['all', 'high', 'medium', 'low'] as const).map(p => (
                                      <button key={p} onClick={() => setStoryPriorityFilter(p)}
                                        className={`px-2.5 py-1.5 text-[10px] font-semibold capitalize transition-all ${storyPriorityFilter === p ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                                        {p}
                                      </button>
                                    ))}
                                  </div>
                                  {(storySearch || storyPriorityFilter !== 'all') && (
                                    <span className="text-[10px] text-slate-400">{pStories.length} shown</span>
                                  )}
                                </div>
                                <table className="w-full text-sm">
                                  <StoryTableHead
                                    columns={STORY_COLUMNS.persona}
                                    variant="tinted"
                                    sort={{ onSort: handleStorySort, icon: storySortIcon }}
                                  />
                                  <tbody className="divide-y divide-slate-100">
                                    {pStories.map(story => (
                                      <StoryRow
                                        key={story.id}
                                        story={story as any}
                                        columns={STORY_COLUMNS.persona}
                                        density="roomy"
                                        isExpanded={expandedStoryIds.has(story.id)}
                                        isSelected={selectedStoryIds.has(story.id)}
                                        onToggleExpand={toggleStory}
                                        onToggleSelect={toggleStorySelection}
                                        onEdit={sessionId ? (s) => setStoryEditTarget(s as any) : undefined}
                                        onClone={sessionId ? (s) => setStoryEditTarget({ ...(s as any), id: `CLONE-${s.id}` }) : undefined}
                                        onDelete={(s) => setDeleteConfirm({ type: 'story', id: s.id, label: storyText(s).substring(0, 80) })}
                                        onToggleLock={sessionId ? (s) => handleToggleStoryLock(s as any) : undefined}
                                        onAvatarReview={sessionId ? (s) => triggerAvatarReview({ type: 'story', storyId: s.id, persona: (s as any).persona }) : undefined}
                                        openDesign={onStartOpenDesignTask ? {
                                          isRunning: openDesigningStoryId === story.id,
                                          isPickerOpen: openDesignPickerTarget === story.id,
                                          onTogglePicker: () => setOpenDesignPickerTarget(prev => prev === story.id ? null : story.id),
                                          onStart: async (skillId) => {
                                            setOpenDesignPickerTarget(null);
                                            setOpenDesigningStoryId(story.id);
                                            try {
                                              await onStartOpenDesignTask(storyText(story as any), skillId);
                                            } finally {
                                              setOpenDesigningStoryId(null);
                                            }
                                          },
                                        } : undefined}
                                        raid={raidControlsForStory(story.id)}
                                        milestone={projectId && milestones.length > 0 ? {
                                          milestones,
                                          onChange: (id) => handleStoryMilestone(story.id, id),
                                        } : undefined}
                                      />
                                    ))}
                                  </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                      </div>
                    </div>
                  );
                })()}

                </div>
              </div>
            )}

            {/* Stories only (no personas) */}
            {sortedPersonas.length === 0 && sortedStories.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                  <BookOpen size={15} className="text-primary" />
                  <h3 className="text-sm font-bold text-slate-600">User Stories</h3>
                  <span className="text-xs text-slate-400">({sortedStories.length})</span>
                </div>
                <table className="w-full text-sm">
                  <StoryTableHead columns={STORY_COLUMNS.bare} />
                  <tbody className="divide-y divide-slate-100">
                    {sortedStories.map(story => (
                      <StoryRow
                        key={story.id}
                        story={story as any}
                        columns={STORY_COLUMNS.bare}
                        isExpanded={expandedStoryIds.has(story.id)}
                        onToggleExpand={toggleStory}
                        onDelete={(s) => setDeleteConfirm({ type: 'story', id: s.id, label: storyText(s).substring(0, 80) })}
                        onToggleLock={sessionId ? (s) => handleToggleStoryLock(s as any) : undefined}
                        onAvatarReview={sessionId ? (s) => triggerAvatarReview({ type: 'story', storyId: s.id, persona: (s as any).persona }) : undefined}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </>
            );
          }
          return null;
        })()}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full mx-4 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 shrink-0">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <h2 className="text-base font-bold text-navy leading-tight">
                Remove this {deleteConfirmNoun(deleteConfirm.type)}?
              </h2>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed">
              <span className="font-medium text-navy">
                {deleteConfirm.label}{deleteConfirm.type === 'story' && deleteConfirm.label.length >= 80 ? '…' : ''}
              </span>
              <br />
              <span className="text-slate-500">
                {deleteConfirmMessage(deleteConfirm.type)}
              </span>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (deleteConfirm.type === 'persona') {
                    onRemovePersona(deleteConfirm.id);
                    setActiveTab(null);
                  } else if (deleteConfirm.type === 'nfr') {
                    if (sessionId) await api.delete(`/api/sessions/${sessionId}/nfrs/${deleteConfirm.id}`);
                    await onReloadData?.();
                  } else if (deleteConfirm.type === 'feature') {
                    if (sessionId) await deleteFeature(sessionId, deleteConfirm.id);
                    await loadFeatures();
                  } else if (deleteConfirm.type === 'adr') {
                    if (sessionId) await deleteADR(sessionId, deleteConfirm.id);
                    await loadADRs();
                  } else {
                    onRemoveRequirement(deleteConfirm.id);
                  }
                  setDeleteConfirm(null);
                }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors"
              >
                <Trash2 size={15} />
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                <Trash2 size={20} className="text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-navy">Clear all requirements?</h2>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed">
              This will permanently delete all <strong>personas</strong> and <strong>user stories</strong> for this session from the database. This cannot be undone — you will need to re-run extraction to regenerate them.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowClearConfirm(false)}
                disabled={clearing}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setClearing(true);
                  try {
                    await onClearAll!();
                  } finally {
                    setClearing(false);
                    setShowClearConfirm(false);
                  }
                }}
                disabled={clearing}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors disabled:opacity-60"
              >
                {clearing ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                {clearing ? 'Deleting...' : 'Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNfrRerunConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                <Sparkles size={20} className="text-primary" />
              </div>
              <h2 className="text-lg font-bold text-navy">Re-extract NFRs?</h2>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed">
              This session already has <strong>{requirements?.nonFunctionalRequirements?.length} NFRs</strong>. Re-running extraction will replace them with a fresh set based on the current transcript.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowNfrRerunConfirm(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={runNfrExtraction}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primary-dark text-white text-sm font-bold transition-colors"
              >
                <Sparkles size={15} /> Re-extract
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdrRerunConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                <Sparkles size={20} className="text-primary" />
              </div>
              <h2 className="text-lg font-bold text-navy">Re-extract ADRs?</h2>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed">
              This session already has <strong>{adrs.length} ADRs</strong>. Re-running extraction will add a fresh set based on the current transcript.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowAdrRerunConfirm(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={runAdrExtraction}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primary-dark text-white text-sm font-bold transition-colors"
              >
                <Sparkles size={15} /> Re-extract
              </button>
            </div>
          </div>
        </div>
      )}

      {makeAvatarPersona && projectId && sessionId && (
        <QuickCreateAvatarModal
          mode="from-persona"
          projectId={projectId}
          sessionId={sessionId}
          persona={makeAvatarPersona}
          onClose={() => setMakeAvatarPersona(null)}
          onCreated={(avatar) => {
            setMakeAvatarPersona(null);
            onAvatarCreated?.(avatar);
          }}
        />
      )}

      {/* Add Persona Modal */}
      {showAddPersona && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-7 max-w-md w-full mx-4 flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-navy flex items-center gap-2"><Users size={18} className="text-purple-600" /> Add Persona</h2>
              <button onClick={() => setShowAddPersona(false)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><X size={18} /></button>
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Name *</label>
                <input
                  value={newPersonaName}
                  onChange={e => setNewPersonaName(e.target.value)}
                  placeholder="e.g. Service Manager"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Role *</label>
                <input
                  value={newPersonaRole}
                  onChange={e => setNewPersonaRole(e.target.value)}
                  placeholder="e.g. Oversees field teams"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Goals <span className="normal-case font-normal text-slate-400">(one per line)</span></label>
                <textarea
                  value={newPersonaGoals}
                  onChange={e => setNewPersonaGoals(e.target.value)}
                  rows={3}
                  placeholder="Reduce response time&#10;Improve visibility"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:border-primary resize-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Pain Points <span className="normal-case font-normal text-slate-400">(one per line)</span></label>
                <textarea
                  value={newPersonaPainPoints}
                  onChange={e => setNewPersonaPainPoints(e.target.value)}
                  rows={3}
                  placeholder="Manual reporting&#10;Data silos"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:border-primary resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowAddPersona(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors">Cancel</button>
              <button
                onClick={handleAddPersona}
                disabled={addPersonaSaving || !newPersonaName.trim() || !newPersonaRole.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary hover:bg-primary-dark text-white text-sm font-bold transition-colors disabled:opacity-50"
              >
                {addPersonaSaving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                Add Persona
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Story Modal */}
      {addStoryForPersona && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-7 max-w-md w-full mx-4 flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-navy flex items-center gap-2"><BookOpen size={18} className="text-blue-600" /> Add Story</h2>
              <button onClick={() => setAddStoryForPersona(null)} className="p-1 rounded hover:bg-slate-100 text-slate-400"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500">Adding to persona: <span className="font-bold text-navy">{addStoryForPersona}</span></p>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Story *</label>
                <textarea
                  value={newStoryText}
                  onChange={e => setNewStoryText(e.target.value)}
                  rows={3}
                  placeholder="As a Service Manager, I want to view live job status so that I can respond to delays immediately."
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:border-primary resize-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Priority</label>
                <div className="flex gap-2">
                  {(['high', 'medium', 'low'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setNewStoryPriority(p)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-bold capitalize transition-colors ${newStoryPriorityButtonClasses(p, newStoryPriority === p)}`}
                    >{p}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Acceptance Criteria <span className="normal-case font-normal text-slate-400">(one per line)</span></label>
                <textarea
                  value={newStoryCriteria}
                  onChange={e => setNewStoryCriteria(e.target.value)}
                  rows={3}
                  placeholder="Status updates every 30s&#10;Works on mobile"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:border-primary resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setAddStoryForPersona(null)} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors">Cancel</button>
              <button
                onClick={handleAddStory}
                disabled={addStorySaving || !newStoryText.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary hover:bg-primary-dark text-white text-sm font-bold transition-colors disabled:opacity-50"
              >
                {addStorySaving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                Add Story
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Image Modal */}
      {showImageModal && onGenerateImage && (
        <GenerateImageModal
          onGenerate={handleGenerateImageFromModal}
          onClose={() => !imageGenLoading && setShowImageModal(false)}
          loading={imageGenLoading}
          countdown={imageGenCountdown}
        />
      )}

      {/* Persona Review Modal */}
      {reviewModalPersonaId && sessionId && (() => {
        const reviewPersona = sortedPersonas.find(p => p.id === reviewModalPersonaId) as import('../services/workshop').ExtractedPersona | undefined;
        if (!reviewPersona) return null;
        const reviewStories = storiesForPersona(reviewPersona.name);
        return (
          <PersonaReviewModal
            sessionId={sessionId}
            persona={reviewPersona}
            stories={reviewStories}
            allStories={stories}
            onClose={() => setReviewModalPersonaId(null)}
            onReloadData={() => onReloadData?.()}
          />
        );
      })()}

      {/* Story Edit Modal */}
      {storyEditTarget && (
        <StoryEditModal
          story={storyEditTarget}
          personas={sortedPersonas.map(p => ({ id: p.id, name: p.name }))}
          features={features}
          sessionId={sessionId}
          persona={(sortedPersonas.find(p => p.name === storyEditTarget.persona) as import('../services/workshop').ExtractedPersona | undefined) ?? null}
          onSave={handleSaveStoryModal}
          onClose={() => setStoryEditTarget(null)}
        />
      )}

      {/* Bulk-tag prompt. Explicit, never a cascade — tagging a feature does
          not move its stories unless the user asks for it here. */}
      {bulkTagPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-5">
              <h3 className="text-base font-bold text-navy">Tag this feature's stories too?</h3>
              <p className="text-sm text-slate-500 mt-1">
                “{bulkTagPrompt.feature.name}” is now in{' '}
                {milestones.find(m => m.milestoneId === bulkTagPrompt.milestoneId)?.name}.{' '}
                {stories.filter(s => (s as any).featureIds?.includes(bulkTagPrompt.feature.id)).length} stor
                {stories.filter(s => (s as any).featureIds?.includes(bulkTagPrompt.feature.id)).length === 1 ? 'y is' : 'ies are'}{' '}
                linked to it. Move them to the same phase?
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
              <button onClick={() => setBulkTagPrompt(null)}
                className="rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors">
                Leave them
              </button>
              <button onClick={confirmBulkTag} disabled={milestoneMutations.assign.isPending}
                className="flex items-center gap-2 rounded-xl h-10 px-4 bg-primary hover:bg-primary-dark text-white text-sm font-bold transition-colors disabled:opacity-60">
                {milestoneMutations.assign.isPending && <Loader2 size={14} className="animate-spin" />}
                Tag them
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RAID Edit / Create Modal */}
      {raidModal && (
        <RaidEditModal
          item={raidModal.mode === 'edit' ? raidModal.item : null}
          defaults={raidModal.mode === 'create' ? { links: raidModal.links } : undefined}
          candidates={raidCandidates}
          saving={raidMutations.create.isPending || raidMutations.update.isPending}
          onSave={handleSaveRaid}
          onResolve={raidModal.mode === 'edit'
            ? (status, resolution) => handleResolveRaid(raidModal.item.raidId, status, resolution)
            : undefined}
          onDelete={raidModal.mode === 'edit' ? handleDeleteRaid : undefined}
          onClose={() => setRaidModal(null)}
        />
      )}

      {/* NFR Edit / Create Modal */}
      {nfrEditTarget && (
        <NFREditModal
          nfr={nfrEditTarget}
          createMode={nfrCreateMode}
          features={features}
          sessionId={sessionId}
          onSave={handleSaveNFRModal}
          onClose={() => { setNfrEditTarget(null); setNfrCreateMode(false); }}
        />
      )}

      {/* Feature Edit / Create Modal */}
      {featureEditTarget && (
        <FeatureEditModal
          feature={featureEditTarget}
          createMode={featureCreateMode}
          personas={sortedPersonas.map(p => ({ id: p.id, name: p.name }))}
          onSave={handleSaveFeatureModal}
          onClose={() => { setFeatureEditTarget(null); setFeatureCreateMode(false); }}
        />
      )}

      {/* ADR Edit / Create Modal */}
      {adrEditTarget && (
        <ADREditModal
          adr={adrEditTarget}
          createMode={adrCreateMode}
          features={features}
          sessionId={sessionId}
          onSave={handleSaveAdrModal}
          onClose={() => { setAdrEditTarget(null); setAdrCreateMode(false); }}
        />
      )}

      {/* Story Merge Preview Modal */}
      {showStoryMergeModal && (
        <MergePreviewModal
          type="story"
          selectedCount={selectedStoryIds.size}
          loading={storyMergeLoading}
          result={storyMergeResult}
          error={storyMergeError}
          onConfirm={confirmStoryMerge}
          onClose={() => { setShowStoryMergeModal(false); setStoryMergeResult(null); setStoryMergeError(null); }}
        />
      )}

      {/* NFR Merge Preview Modal */}
      {showNfrMergeModal && (
        <MergePreviewModal
          type="nfr"
          selectedCount={selectedNfrIds.size}
          loading={nfrMergeLoading}
          result={nfrMergeResult}
          error={nfrMergeError}
          onConfirm={confirmNfrMerge}
          onClose={() => { setShowNfrMergeModal(false); setNfrMergeResult(null); setNfrMergeError(null); }}
        />
      )}


    </div>
  );
}
